import {
  CHANNEL_ARC_CANCELLED,
  CHANNEL_ARC_SETTLED,
  CHANNEL_ARC_THINKING,
  CHANNEL_ARC_TURN_TEXT,
  CHANNEL_MEMBER_JOINED,
  CHANNEL_MEMBER_LEFT,
  CHANNEL_MESSAGE_CREATED,
  CHANNEL_MESSAGE_DELETED,
  CHANNEL_MESSAGE_UPDATED,
  CHANNEL_READ_STATE_CHANGED,
  CHANNEL_REACTION_CHANGED,
  CHANNEL_SCHEDULED_MESSAGE_CHANGED,
  CHANNEL_TYPING_START,
  CHANNEL_TYPING_STOP,
  CHANNEL_UPDATED,
} from '@common/constants/webSockets'
import {
  CHANNEL_PREVIEW_MAX_CHARS,
  countedUnread,
  isConversationMuted,
  isThreadRowMuted,
} from '@common/helpers/channelNotifications'
import { displayName, shortDisplayName } from '@common/helpers/userDisplay'
import {
  ARC_DISPATCH_GRACE_MS,
  AtlasAIMessageStatus,
  IAtlasAITurnText,
  ICitation,
} from '@common/interfaces/atlasai'
import {
  ChannelKind,
  ChannelMemberRole,
  ChannelMessageKind,
  ChannelNotificationLevel,
  AimPinTargetType,
  AimNoteScopeType,
  IAimNote,
  IArcHome,
  IChannel,
  IChannelHome,
  IChannelListItem,
  IChannelMessage,
  IChannelMessageReactionSummary,
  IChannelTypingUser,
  IChannelShareLanding,
  IConversationGroup,
  IConversationListItem,
  IPersonDirectoryEntry,
  IPersonHome,
  IRecentConversationItem,
  IScheduledChannelMessage,
  IScheduledRunGroup,
  OUTSTANDING_SCHEDULED_MESSAGE_STATUSES,
  IConversationSearchResult,
  IUnreadConversationSummary,
  RECENT_CONVERSATIONS_FLOOR,
  RECENT_CONVERSATIONS_PAGE,
  SearchOrder,
} from '@common/interfaces/channels'
import { mentionsArc } from '@common/interfaces/mentions'
import { Feedback } from '@common/interfaces/prospects'
import { ThreadsFilter } from '@common/interfaces/threadsFilter'
import { IAimStatus } from '@common/interfaces/user'
import { RawDraftContentState } from 'draft-js'
import { Action } from 'redux'
import type { ISearchFilters } from '../../api/channels'
import {
  PROFILE_HYDRATED,
  PROFILE_SUCCESS,
  UPDATE_AIM_PROFILE_SUCCESS,
  UPDATE_PROFILE_SUCCESS,
} from '../profile/actions'
import * as actions from './actions'
import { ChannelSidePanel } from './actions'
import {
  ICachedTimeline,
  IOutboxEntry,
  IOutboxSendData,
  isUnsentMessage,
  withOfflineState,
} from './offline'
import {
  IEmbeddedPane,
  ISplitPane,
  emptyTimeline,
  isOpenBesideMain,
  withSplitPanes,
} from './splitPanes'

/** A message plus the client-only send-state flags. */
export interface IChannelMessageView extends IChannelMessage {
  /** Optimistically rendered, not yet acknowledged by the server. */
  isPending?: boolean
  /** The send failed. Kept on screen so the body isn't lost and can be retried. */
  isFailed?: boolean
  /**
   * Why it failed, when the server said. Absent for a request that never got an
   * answer — which is the only case the "check your connection" fallback is
   * actually describing.
   */
  failedReason?: string
  /** Couldn't reach the server; waiting in the outbox to send on reconnect. */
  isQueued?: boolean
  /** What an unsent row was sent with, so Retry repeats it exactly (attachments, reply). */
  unsentData?: IOutboxSendData
}

/**
 * A channel member as the client holds one — the display-resolved subset of the
 * shared `IChannelMember`, which also carries the membership bookkeeping (read
 * cursors, notification level) no UI here reads.
 */
export interface IChannelMember {
  userId: string
  name: string
  avatar: string | null
  role: ChannelMemberRole
  joinedAt?: string
  /** Expiry already applied by the server; null when they have no status set. */
  aimStatus?: IAimStatus | null
}

export interface IChannelDetail extends IChannel {
  /** Shown from the timeline cache while the fresh detail loads; its counts may be stale. */
  isCached?: boolean
  /** Arc is a participant — the model picker only means something when it is. */
  hasArc: boolean
  /**
   * What Arc will actually run on here, resolved server-side (see
   * `resolveChannelArcModel`): this conversation's own `arcModel`, the model its
   * bound session is already running on, or the viewer's Arc Settings default.
   *
   * What the picker shows while `arcModel` is null. Showing the global default
   * there instead is what made a new chat read "Opus" and the conversation it
   * created read "Sonnet" — the turn had been running on the user's own default
   * the whole time.
   */
  arcEffectiveModel: string
  /**
   * The Arc bot's user id, or null when Arc isn't resolvable. Lets the timeline
   * spot Arc in a message's `mentions` and so offer Retry only on the messages
   * that actually started a run — see channels/Get.
   */
  arcUserId: string | null
  /** Set on an Arc Live conversation: its session, whose transcript panel the header toggles. */
  arcLiveSessionId?: string | null
  isMember: boolean
  /**
   * Whether this viewer may rename the conversation — `canRenameChannel` on the
   * server, the same predicate `channels/Update` authorizes the write with, so a
   * rename affordance is never offered where the save would be refused.
   *
   * False for a channel unless the viewer owns or administers it, and false for
   * the canonical DM, which is titled from its participants rather than named.
   */
  canRename: boolean
  /**
   * The caller's own `channel_member` settings for this channel, straight from
   * `channels/Get`. Carried here because the sidebar row is not always there to
   * read them from: a conversation grouped under a person or automation is
   * absent from the channel list by design, and its group is only fetched once
   * expanded — so a deep link into one has nothing else to fall back on.
   */
  isPinned: boolean
  notificationLevel: ChannelNotificationLevel | null
  mutedUntil: string | null
  /** A thread unmuted on its own while its group is muted. Absent from an older server. */
  overridesGroupMute?: boolean
  unreadCount: number
  mentionCount: number
  memberCount: number
  lastReadMessageId: string | null
  lastReadAt: string | null
  members: IChannelMember[]
}

/** Arc's working state, reasoning trace and sources for one run. */
export interface IChannelArcProgress {
  /**
   * Which conversation this run belongs to. This map is NOT scoped to the open
   * channel — CHANNEL_ARC_* events are membership-scoped, so every channel the
   * reader belongs to lands here and the Threads inbox draws them all — so a
   * per-channel answer may only settle the entries it could have covered.
   */
  channelId: string | null
  /**
   * When `seedPendingArcRun` stamped an entry for a dispatch that has no session
   * yet; null once the server can account for the run. Bounds how long that entry
   * is exempt from being settled, on the same `ARC_DISPATCH_GRACE_MS` the server
   * uses, so a dispatch that died never leaves the line turning indefinitely.
   */
  pendingSince: number | null
  isArcWorking: boolean
  thinking: string | null
  turnTexts: IAtlasAITurnText[]
  /**
   * What the turn cited. Only ever arrives from the arc-progress fetch: citations
   * are written when a turn completes, so the live CHANNEL_ARC_* events below
   * carry none and must preserve whatever is already here rather than reset it.
   */
  citations: ICitation[]
  /**
   * The Atlas session working this conversation, used to scope the approval card. Known
   * from the first live event of a run, so a card can appear mid-run rather than
   * only once Arc has replied. Null until Arc has run here at all.
   */
  sessionId: string | null
  /**
   * The turn this run is. A conversation is one session and two mentions can be answered at
   * once, so this — not the session — is what ties a reply or a Stop to one trigger's line.
   * Null until the run's first event names it.
   */
  turnId: string | null
  /** Files the turn produced, rendered as chips rather than links in the body. */
  outputFiles: Array<{ boxFileId: string; name: string; action?: string }>
  unverifiedFileClaim: boolean
  /** Typed as the enum, not a string: the row compares against it. */
  status: AtlasAIMessageStatus | null
  errorDetails: string | null
  feedbackType: Feedback | null
  feedbackComment: string | null
  /**
   * Which model actually answered, and — when failover replaced the chosen one —
   * which model was asked for. Written when the turn completes, so the live
   * CHANNEL_ARC_* events carry none and must preserve what the fetch put here.
   */
  modelUsed: string | null
  modelFallbackFrom: string | null
}

export interface IChannelSearchResult {
  messageId: string
  channelId: string
  channelSlug: string | null
  channelName: string | null
  channelKind: string
  /** The group a thread belongs to, shown beside its name. */
  groupName?: string | null
  senderUserId: string
  senderName: string
  /** Nickname whole, else first name — see `shortDisplayName` on the server. */
  senderShortName?: string
  headline: string
  createdAt: string
}

/**
 * What a homepage slice is currently waiting on.
 *
 * A homepage is one `Promise.all` of seven queries, so responses arrive out of
 * order as a matter of course: a slow group A lands after the user has moved to
 * group B, and a slow first keystroke after a fast third. Comparing the whole
 * signature settles navigation, search and load-more with one check — a bare
 * target id only settles the first, and search races on one target are the
 * common case now that the search box refetches.
 */
export interface IHomeRequest {
  targetId: string | null
  search: string
  limit: number | null
}

const homeRequestOf = (params: unknown): IHomeRequest => {
  const asked = (params ?? {}) as {
    channelId?: string
    userId?: string
    search?: string
    conversationLimit?: number
  }
  return {
    targetId: asked.channelId ?? asked.userId ?? null,
    search: asked.search ?? '',
    limit: asked.conversationLimit ?? null,
  }
}

/** A response belongs to the slice only if it answers the request still outstanding. */
const answersPending = (pending: IHomeRequest | null, params: unknown): boolean => {
  if (!pending) return false
  const answered = homeRequestOf(params)
  return (
    pending.targetId === answered.targetId &&
    pending.search === answered.search &&
    pending.limit === answered.limit
  )
}

/** One conversation's loaded history — the main conversation's, or a split pane's. */
export interface IChannelTimeline {
  channelId: string | null
  items: IChannelMessageView[]
  hasMore: boolean
  /** Both halves of the timeline cursor — see ListChannelMessages. */
  nextCursor: string | null
  nextCursorId: string | null
  /**
   * True when `items` is a window from the middle of the channel's history
   * rather than the live tail — set by a deep link that had to jump back (see
   * GET_MESSAGE_CONTEXT). While it holds, incoming messages must not be
   * appended: the newest row on screen is an old one, and appending would draw
   * a live message as its neighbour.
   */
  hasNewer: boolean
  isLoading: boolean
  isLoadingMore: boolean
  /** The first page failed, so an empty `items` means "couldn't load", not "no messages". */
  hasFailed: boolean
  /** Opened from the timeline cache; the network refresh is still to land. */
  isFromCache?: boolean
  /** Where to put the reader back, from the cache; null means land as usual. */
  restoreScrollTop?: number | null
  /** When the server last delivered the newest page, so a cached reopen knows whether it can merge. */
  cachedAt?: number | null
}

export interface IChannelsState {
  currentUserId: string | null
  /**
   * The viewer's own display name and avatar, captured from the profile load.
   *
   * Held here so an optimistic message can render with its author already attached.
   * Without it the row fell back to ChannelMessageRow's `?? 'Unknown'`, so your own
   * message showed as "Unknown" until the server echo replaced it — most visible on a
   * Arc reply, which always gets its own header rather than being grouped.
   *
   * Nickname first, matching what the server puts on the saved message: a name
   * resolved any other way swaps on screen the moment the send completes.
   */
  currentUserName: string | null
  /** The same name in its compact form — `shortDisplayName`, a nickname kept whole. */
  currentUserShortName: string | null
  currentUserAvatar: string | null
  channels: IChannelListItem[]
  isLoadingChannels: boolean
  browse: {
    channels: Array<IChannel & { memberCount: number; isMember: boolean }>
    total: number
    isLoading: boolean
  }
  activeChannelId: string | null
  activeChannel: IChannelDetail | null
  /** The channel an open settings dialog is editing — see `getSettingsChannel`. */
  settingsChannel: IChannelDetail | null
  /**
   * The Arc-model write still on screen, and the value to put back if it is
   * refused (see `setChannelArcModel`).
   *
   * The picker stays enabled while a write is in flight, so several picks can be
   * outstanding at once. `requestId` is how a refused one tells whether the value
   * it put on screen is still there to roll back — the same "is this still the
   * request I'm looking at?" correlation `isStaleSearch` does for search.
   *
   * `rollbackModel` is held here rather than read off `activeChannel` when the
   * failure lands, because by then that value is itself optimistic: a burst whose
   * writes all fail would otherwise unwind to the first failed pick instead of to
   * what the server actually stores.
   */
  pendingArcModelWrite: { requestId: number; rollbackModel: string | null } | null
  /**
   * The row an in-flight archive removed, and where it sat.
   *
   * The removal is optimistic, so a refusal has to put it back — and back in
   * place: the list is ordered pinned-then-recent by the server, so appending it
   * would move a conversation the server never touched.
   */
  pendingConversationArchive: { item: IRecentConversationItem; index: number } | null
  messages: IChannelTimeline
  /** Conversations open beside the main one, left to right — see splitPanes.ts. */
  splitPanes: ISplitPane[]
  /** Record pages' own conversations, open beside AIM's — see splitPanes.ts. */
  embeddedPanes: IEmbeddedPane[]
  typingUsers: Record<string, IChannelTypingUser[]>
  /**
   * Arc's live reasoning, keyed by the message that summoned it. Seeded by the
   * arc-progress fetch and appended to by CHANNEL_ARC_* events, so the reasoning
   * panel and the timeline read the same state.
   */
  arcProgress: Record<string, IChannelArcProgress>
  /**
   * Per channel, bumped by every live event that changes what Arc is working on
   * there — a sequence marker, not a count. Decides whether an arc-runs response
   * is still allowed to speak; see `ARC_RUNS_SUCCESS`.
   *
   * Keyed by channel because the events that bump it are not: `CHANNEL_ARC_*`
   * fans out to every channel the reader belongs to, so a single counter let an
   * unrelated run elsewhere void the response for the channel being opened — and
   * nothing re-asks, so its indicators stayed missing until the next switch.
   */
  arcProgressGeneration: Record<string, number>
  /**
   * The newest arc-runs request and the generation it began at, held as one
   * record so they cannot drift apart. Mirrors the Arc plan slice, which guards
   * the identical race (see redux/atlasai/reducer.ts).
   */
  arcRunsRequest: { channelId: string; id: string; generation: number } | null
  /**
   * Sidebar conversation groups, and the conversations inside whichever are
   * expanded. Kept keyed by `dmKey` rather than flattened into one list because
   * two groups can be open at once and their fetches can land out of order — the
   * key is what routes a response to the group that asked for it.
   */
  conversationGroups: {
    items: IConversationGroup[]
    isLoading: boolean
  }
  /** Automations with scheduled runs — the sidebar's other group kind. */
  scheduledRunGroups: {
    items: IScheduledRunGroup[]
    isLoading: boolean
  }
  /** The viewer's own send-later messages that have not settled, soonest first. */
  scheduledMessages: {
    items: IScheduledChannelMessage[]
    isLoading: boolean
  }
  /**
   * The Conversations section: one flat, recency-ordered list across every
   * grouping, unread first.
   *
   * Held separately from `conversationsByGroup` rather than derived from it: that
   * map only holds the groups someone has expanded, and this list has to be
   * complete because it is the only place unread is surfaced.
   */
  recentConversations: {
    items: IRecentConversationItem[]
    /** The server's total, including rows past the loaded window — what the app badge reads. */
    unreadTotal: number
    isLoading: boolean
    /** The Threads filter in effect; null until chosen, when the saved setting applies. */
    filter: ThreadsFilter | null
    /** How many unpinned threads the window holds, grown by "Show more threads". */
    recentCount: number
    /** Whether the server has older threads past the window. */
    hasMore: boolean
    /** Every unread conversation the nav badge counts, whatever the filter loaded. */
    unreadConversations: IUnreadConversationSummary[]
    /** Group threads that alert only on a mention, including ones no filter has loaded. */
    quietThreadIds: string[]
    /** Every conversation muted for the viewer, including ones no filter has loaded. */
    mutedConversationIds: string[]
    /** Muted conversations holding unread — what lights the Threads filter's dot. */
    mutedUnreadIds: string[]
    /** When the soonest timed mute on any muted conversation, loaded or not, ends. */
    nextMuteExpiry: string | null
  }
  /** Everyone on the team — the People directory. */
  people: {
    items: IPersonDirectoryEntry[]
    isLoading: boolean
  }
  /**
   * The homepage currently open, of either kind. One slot rather than a map: only
   * one homepage is on screen at a time, and caching the others would show stale
   * tasks and pins on the way back to them.
   */
  channelHome: {
    channelId: string | null
    data: IChannelHome | null
    isLoading: boolean
    pending: IHomeRequest | null
    /**
     * Whether the last settled request failed.
     *
     * Without it "never asked" and "asked and failed" are the same state — both
     * are `isLoading: false, data: null` — so every homepage opened on the
     * failure copy before its own effect had dispatched anything.
     */
    hasFailed: boolean
  }
  personHome: {
    userId: string | null
    data: IPersonHome | null
    isLoading: boolean
    pending: IHomeRequest | null
    hasFailed: boolean
  }
  /** Arc's homepage. One slot, like the other two — only one is ever on screen. */
  arcHome: {
    data: IArcHome | null
    isLoading: boolean
    pending: IHomeRequest | null
    hasFailed: boolean
  }
  /**
   * Keyed by group id, which is a `dmKey` for a participant set and an
   * `automationId` for an automation's runs. One map rather than two because both
   * are paged through the same request and hold the same row shape; the key space
   * cannot collide (a uuid is never a colon-joined id set).
   */
  conversationsByGroup: Record<
    string,
    {
      items: IConversationListItem[]
      total: number
      hasMore: boolean
      isLoading: boolean
      /**
       * The loaded window may no longer be the server's first N rows, so the group
       * has to be read again from the start before it can be paged.
       *
       * Set when a mutation reorders the set under us in a direction we cannot
       * resolve from here — see `patchConversationInGroups`. The rows stay on
       * screen while it is true; the open run list refetches the group and the
       * fresh page clears it.
       */
      needsReload: boolean
      /** Which request failed last — a fresh load or the next page — so a retry repeats it. */
      failedLoad: 'first' | 'next' | null
      /**
       * Where the next page starts, counted in the server's rows rather than ours.
       *
       * Paging is `LIMIT/OFFSET` over an ordering that moves underneath us, so a
       * page can repeat a row we already hold. We drop the repeat before rendering,
       * which means our row count falls behind the offset we actually read to — and
       * asking for the next page at `items.length` would re-read the overlap
       * forever, stranding everything past it. So the cursor advances by the size
       * of the page the server sent, not by how much of it survived the dedupe.
       */
      nextOffset: number
    }
  >
  search: {
    query: string
    /** Scope of the request in flight — `null` is "everything you can see". */
    channelId: string | null
    /** Order and the people/date filters, the rest of the request's identity. */
    orderBy: SearchOrder
    filterKey: string
    results: IChannelSearchResult[]
    isLoading: boolean
    hasMore: boolean
  }
  /** The Search surface's conversation matches, or its full list with no query. */
  conversationSearch: {
    requestKey: string
    results: IConversationSearchResult[]
    isLoading: boolean
  }
  /** Which auxiliary panel the conversation view is showing, if any. */
  sidePanel: ChannelSidePanel
  /**
   * People the last sent message @-mentioned who will never see it — not members,
   * and not Arc, which answers a mention without joining. Resolved by SendMessage,
   * which is the only side that can tell the two apart. Sender-only and never
   * persisted: it drives the offer to add them and nothing else.
   */
  mentionInvite: { channelId: string; users: Array<{ id: string; name: string }> } | null
  /**
   * The conversation behind a `?share=` link, while the landing modal decides
   * whether to preview it or send the viewer straight in.
   *
   * `error` rather than a thrown failure: a share link that has been revoked, or
   * points at a deleted conversation, is a normal thing to arrive with and the modal
   * says so. `shareId` is carried so a response can be matched against the token
   * still on screen.
   */
  shareLanding: {
    shareId: string | null
    data: IChannelShareLanding | null
    isLoading: boolean
    isJoining: boolean
    error: string | null
  }
  /** Sends not yet acknowledged, by clientMessageId. Persisted per user — see offline.ts. */
  outbox: Record<string, IOutboxEntry>
  /** Recently opened timelines, so reopening one renders at once. See offline.ts. */
  messageCache: Record<string, ICachedTimeline>
  /** Most recent first; its tail is what the LRU evicts. */
  messageCacheOrder: string[]
  channelDetailCache: Record<string, IChannelDetail>
  /** Since when the socket has been continuously connected; null while it isn't. */
  socketConnectedSince: number | null
}

const initialState: IChannelsState = {
  currentUserId: null,
  currentUserName: null,
  currentUserShortName: null,
  currentUserAvatar: null,
  channels: [],
  isLoadingChannels: false,
  browse: { channels: [], total: 0, isLoading: false },
  activeChannelId: null,
  activeChannel: null,
  settingsChannel: null,
  pendingArcModelWrite: null,
  pendingConversationArchive: null,
  messages: emptyTimeline(null),
  splitPanes: [],
  embeddedPanes: [],
  typingUsers: {},
  arcProgress: {},
  arcProgressGeneration: {},
  arcRunsRequest: null,
  conversationGroups: { items: [], isLoading: false },
  scheduledRunGroups: { items: [], isLoading: false },
  scheduledMessages: { items: [], isLoading: false },
  recentConversations: {
    items: [],
    unreadTotal: 0,
    isLoading: false,
    filter: null,
    recentCount: RECENT_CONVERSATIONS_FLOOR,
    hasMore: false,
    unreadConversations: [],
    quietThreadIds: [],
    mutedConversationIds: [],
    mutedUnreadIds: [],
    nextMuteExpiry: null,
  },
  people: { items: [], isLoading: false },
  channelHome: { channelId: null, data: null, isLoading: false, pending: null, hasFailed: false },
  personHome: { userId: null, data: null, isLoading: false, pending: null, hasFailed: false },
  arcHome: { data: null, isLoading: false, pending: null, hasFailed: false },
  conversationsByGroup: {},
  shareLanding: { shareId: null, data: null, isLoading: false, isJoining: false, error: null },
  search: {
    query: '',
    channelId: null,
    orderBy: SearchOrder.Relevance,
    filterKey: '',
    results: [],
    isLoading: false,
    hasMore: false,
  },
  conversationSearch: { requestKey: '', results: [], isLoading: false },
  sidePanel: null,
  mentionInvite: null,
  outbox: {},
  messageCache: {},
  messageCacheOrder: [],
  channelDetailCache: {},
  socketConnectedSince: null,
}

type ChannelsAction = Action & {
  data?: unknown
  params?: Record<string, unknown>
  error?: Error & { code?: string; status?: number }
}

// --- helpers -----------------------------------------------------------------

/**
 * Which group a conversations response belongs to.
 *
 * Both group kinds page through the same action, so the key is whichever
 * selector the request used — a `dmKey` for a participant set, an `automationId`
 * for an automation's runs.
 */
const conversationGroupKey = (action: ChannelsAction): string | undefined =>
  (action.params?.dmKey as string | undefined) ??
  (action.params?.automationId as string | undefined)

/**
 * Whether a search response belongs to a request that has already been replaced.
 *
 * A request is identified by what was asked for — the text *and* the scope — since
 * "cool" and "cool, only in #general" produce responses that are otherwise
 * indistinguishable. REQUEST stamps both onto the slice, so anything arriving with
 * a different pair finished late and lost.
 *
 * The typeahead is what makes this reachable: it searches on a 250ms debounce, and
 * ticking the scope box fires its own request immediately, so two searches for the
 * same text at different scopes are routinely in flight together.
 */
/** Identity of the people and date filters; the people are order-insensitive. */
export const searchFilterKeyOf = (filters: ISearchFilters | undefined): string =>
  [
    [...(filters?.memberIds ?? [])].sort().join(','),
    filters?.dateFrom ?? '',
    filters?.dateTo ?? '',
  ].join('|')

const isStaleSearch = (action: ChannelsAction, state: IChannelsState): boolean =>
  (action.params?.query as string) !== state.search.query ||
  ((action.params?.channelId as string | undefined) ?? null) !== state.search.channelId ||
  ((action.params?.orderBy as SearchOrder | undefined) ?? SearchOrder.Relevance) !==
    state.search.orderBy ||
  searchFilterKeyOf(action.params) !== state.search.filterKey

/**
 * Mute takes effect for alerts at once rather than at the next refetch. A group's own
 * id is harmless here; its threads follow on the refetch the server broadcasts.
 */
const withMutedConversationId = (
  recent: IChannelsState['recentConversations'],
  channelId: string,
  isMuted: boolean,
): IChannelsState['recentConversations'] => {
  const others = recent.mutedConversationIds.filter((id) => id !== channelId)
  return {
    ...recent,
    mutedConversationIds: isMuted ? [...others, channelId] : others,
    // Unmuted, its unread is back in the ordinary badges rather than the filter's dot.
    mutedUnreadIds: isMuted
      ? recent.mutedUnreadIds
      : recent.mutedUnreadIds.filter((id) => id !== channelId),
  }
}

/** Fetched for a filter or window other than the one now in effect. */
const isStaleThreadsResponse = (action: ChannelsAction, state: IChannelsState): boolean => {
  const filter = action.params?.filter as ThreadsFilter | undefined
  if (filter === undefined) return false
  return (
    filter !== state.recentConversations.filter ||
    action.params?.recentCount !== state.recentConversations.recentCount
  )
}

const conversationSearchKeyOf = (params: ChannelsAction['params']): string =>
  `${((params?.query as string | undefined) ?? '').trim()}|${searchFilterKeyOf(params)}`

const upsertById = (
  items: IChannelMessageView[],
  message: IChannelMessageView,
): IChannelMessageView[] => {
  const index = items.findIndex((item) => item.id === message.id)
  if (index === -1) return [...items, message]
  const next = items.slice()
  // Preserve client-only flags the server response doesn't carry.
  next[index] = {
    ...next[index],
    ...message,
    isPending: false,
    isFailed: false,
    failedReason: undefined,
  }
  return next
}

const mapById = (
  items: IChannelMessageView[],
  id: string,
  update: (message: IChannelMessageView) => IChannelMessageView,
): IChannelMessageView[] => items.map((item) => (item.id === id ? update(item) : item))

/**
 * The row a send occupies before the server has answered, built from what the user
 * typed and who they are. Null when the action carries too little to build one.
 *
 * Built from the action rather than held anywhere, so SEND_MESSAGE_FAILURE can
 * rebuild the same row when the send never got one.
 */
const buildOptimisticMessage = (
  state: IChannelsState,
  params: ChannelsAction['params'],
): IChannelMessageView | null => {
  const { channelId, tempId, data } = (params ?? {}) as {
    channelId?: string
    tempId?: string
    data?: { content?: string; contentState?: IChannelMessage['contentState'] }
  }
  if (!channelId || !tempId || !state.currentUserId) return null

  const now = new Date().toISOString()
  return {
    id: tempId,
    channelId,
    senderUserId: state.currentUserId,
    kind: 'message' as IChannelMessage['kind'],
    contentState: data?.contentState ?? null,
    content: data?.content ?? '',
    mentions: [],
    arcSessionId: null,
    // A message the user just typed has no Arc turn behind it.
    arcTurnId: null,
    pinnedAt: null,
    pinnedByUserId: null,
    editedAt: null,
    createdAt: now,
    updatedAt: now,
    // Attached up front so the row never renders as "Unknown" while in flight —
    // both forms, since the header reads `senderShortName` first.
    senderName: state.currentUserName ?? undefined,
    senderShortName: state.currentUserShortName ?? undefined,
    senderAvatar: state.currentUserAvatar,
    isPending: true,
  }
}

/**
 * A first page of history, with the sender's own un-sent rows carried over.
 *
 * A pending or failed message exists only on this client, so a page fetched from
 * the server can only ever drop it — which is what emptied a brand new
 * conversation whose first message failed.
 */
const withUnsentRows = (
  existing: IChannelMessageView[],
  incoming: IChannelMessageView[],
): IChannelMessageView[] => {
  const incomingIds = new Set(incoming.map((item) => item.id))
  const unsent = existing.filter((item) => isUnsentMessage(item) && !incomingIds.has(item.id))
  return unsent.length ? [...incoming, ...unsent] : incoming
}

/**
 * Patches one message in a homepage's Pinned block; `update` returning null drops it.
 * The homepage is fetched once, so without this an edit or delete made anywhere
 * stays stale there until the page is reloaded.
 */
const patchHomePinned = <T extends { data: { pinnedMessages: IChannelMessage[] } | null }>(
  home: T,
  messageId: string,
  update: (message: IChannelMessage) => IChannelMessage | null,
): T => {
  if (!home.data?.pinnedMessages?.some((item) => item.id === messageId)) return home
  const pinnedMessages = home.data.pinnedMessages.flatMap((item) => {
    if (item.id !== messageId) return [item]
    const next = update(item)
    return next ? [next] : []
  })
  return { ...home, data: { ...home.data, pinnedMessages } }
}

type MessageSurfaces = Pick<IChannelsState, 'messages' | 'channelHome' | 'personHome'>

/**
 * Apply a server-updated message to the timeline and the homepages' Pinned blocks.
 *
 * Merged rather than replaced, so client-only flags (`isPending`, `isFailed`) and
 * any field the payload omits survive. Every service that changes a message returns
 * the whole message and broadcasts the same shape over `CHANNEL_MESSAGE_UPDATED`, so
 * edit, pin, dismiss and the socket frame all land through here. An unpin drops the
 * message from the Pinned blocks.
 */
const applyMessageUpdate = (
  state: IChannelsState,
  updated: IChannelMessageView,
): MessageSurfaces => {
  const updatePinned = (item: IChannelMessage) =>
    updated.pinnedAt === null ? null : { ...item, ...updated }
  return {
    messages: {
      ...state.messages,
      items: mapById(state.messages.items, updated.id, (item) => ({ ...item, ...updated })),
    },
    channelHome: patchHomePinned(state.channelHome, updated.id, updatePinned),
    personHome: patchHomePinned(state.personHome, updated.id, updatePinned),
  }
}

/** Remove a deleted message from the timeline and the homepages' Pinned blocks. */
const applyMessageDelete = (state: IChannelsState, messageId: string): MessageSurfaces => ({
  messages: {
    ...state.messages,
    items: state.messages.items.filter((item) => item.id !== messageId),
  },
  channelHome: patchHomePinned(state.channelHome, messageId, () => null),
  personHome: patchHomePinned(state.personHome, messageId, () => null),
})

/** Merge a page of messages into existing ones, de-duped by id and time-ordered. */
const mergeMessages = (
  existing: IChannelMessageView[],
  incoming: IChannelMessageView[],
): IChannelMessageView[] => {
  const byId = new Map(existing.map((item) => [item.id, item]))
  for (const message of incoming) {
    const previous = byId.get(message.id)
    byId.set(message.id, previous ? { ...previous, ...message } : message)
  }
  return [...byId.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

const updateChannelItem = (
  channels: IChannelListItem[],
  channelId: string,
  update: (channel: IChannelListItem) => IChannelListItem,
): IChannelListItem[] =>
  channels.map((channel) => (channel.id === channelId ? update(channel) : channel))

/**
 * Sidebar ordering for grouped conversations: pinned first, then most recently
 * active. Mirrors `channels/selectors.ts`'s `byPinnedThenRecent` and the server's
 * own `ORDER BY` in `ListConversations` — kept as a separate comparator because
 * `IConversationListItem` has no `createdAt` to fall back to for a never-posted
 * conversation, only a nullable `lastMessageAt`.
 */
const byPinnedThenRecentConversation = (a: IConversationListItem, b: IConversationListItem) => {
  if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1
  if (a.lastMessageAt !== b.lastMessageAt) {
    if (!a.lastMessageAt) return 1
    if (!b.lastMessageAt) return -1
    return b.lastMessageAt.localeCompare(a.lastMessageAt)
  }
  return a.name.localeCompare(b.name) || a.id.localeCompare(b.id)
}

/**
 * Patches one conversation in a homepage's Threads block, re-sorted the way the
 * server orders it. No cursor to repair: "show more" refetches the whole window.
 */
const patchHomeConversation = <
  T extends { data: { conversations: IConversationListItem[] } | null },
>(
  home: T,
  channelId: string,
  update: (item: IConversationListItem) => IConversationListItem,
): T => {
  if (!home.data?.conversations.some((item) => item.id === channelId)) return home
  const conversations = home.data.conversations
    .map((item) => (item.id === channelId ? update(item) : item))
    .sort(byPinnedThenRecentConversation)
  return { ...home, data: { ...home.data, conversations } }
}

/**
 * Patches one conversation wherever it is loaded across `conversationsByGroup`,
 * re-sorting the group it lives in and repairing that group's paging cursor if the
 * change pushed the row out of the window we hold. A conversation is only ever in
 * one group's loaded page at a time, so this stops at the first match rather than
 * scanning every group needlessly.
 *
 * Keeping the loaded window in step with the server's own ordering matters because
 * `ListConversations` pages with `LIMIT/OFFSET` over
 * `is_pinned DESC, last_message_at DESC NULLS LAST, display_name ASC`, and the
 * sidebar reads the next page from a cursor over those same rows. Any mutation
 * touching one of those three keys reorders that set server-side — so unless what
 * we hold is still exactly the server's first N rows, the cursor points into the
 * wrong place and a conversation is duplicated or skipped.
 *
 * Whether that can happen depends on the direction the row can move, which only
 * the caller knows — hence `canSinkPastThePage`:
 *
 * - **Pinning** cannot: the row moves to index 0, everything else shifts down one,
 *   and our N rows are still the server's first N.
 * - **Unpinning** can: the row drops out of the pinned block, and if it lands
 *   anywhere but the last slot its new position is genuine (its `lastMessageAt`
 *   beats every row below it) so the prefix still matches.
 * - **Renaming** can too, for the same reason in a different key: the server breaks
 *   ties on `display_name`, so among the rows sharing a `lastMessageAt` — most of a
 *   fresh group, which has never been posted in — the name is what decides the
 *   order, and a new name can sort the row past everything we hold.
 *
 * Landing in the *last* slot with more rows unread is the ambiguous case: the row
 * may truly belong there, or anywhere past what we have loaded, and we cannot tell
 * from here. So we drop it — `total` stays put because the conversation still
 * exists, and it comes back on the next "Show more" or re-expand at whatever
 * position the server actually gives it.
 */
const patchConversationInGroups = (
  conversationsByGroup: IChannelsState['conversationsByGroup'],
  channelId: string,
  update: (item: IConversationListItem) => IConversationListItem,
  { canSinkPastThePage }: { canSinkPastThePage: boolean },
): IChannelsState['conversationsByGroup'] => {
  for (const [groupId, group] of Object.entries(conversationsByGroup)) {
    if (!group.items.some((item) => item.id === channelId)) continue

    const items = group.items
      .map((item) => (item.id === channelId ? update(item) : item))
      .sort(byPinnedThenRecentConversation)
    const mayHaveSunk = canSinkPastThePage && group.hasMore && items.at(-1)?.id === channelId

    return {
      ...conversationsByGroup,
      [groupId]: {
        ...group,
        items,
        // Landing last is as far as we can reason: whether the row genuinely moved
        // past the window turns on the `lastMessageAt` of rows we have not fetched.
        // Dropping it on that suspicion is what made a renamed conversation vanish
        // from the sidebar the moment it was the bottom row. So the row stays put
        // and the group is marked for a reread instead — the server resolves in one
        // request what we can only guess at, and nothing disappears meanwhile.
        needsReload: group.needsReload || mayHaveSunk,
      },
    }
  }
  return conversationsByGroup
}

/**
 * Patches a renamed conversation wherever it is loaded across `conversationsByGroup`.
 *
 * Grouped conversations are a store of their own — a conversation nested under a
 * person or an automation is deliberately absent from the flat `channels` list —
 * so patching `channels` and `activeChannel` alone leaves the sidebar row the
 * rename was made *from* still showing the old name. That is true of a rename this
 * tab made (`UPDATE_CHANNEL_SUCCESS`) and of one it merely heard about
 * (`CHANNEL_UPDATED`), which is why both route through here.
 */
const renameConversationInGroups = (
  conversationsByGroup: IChannelsState['conversationsByGroup'],
  channelId: string,
  name: string,
): IChannelsState['conversationsByGroup'] => {
  // `CHANNEL_UPDATED` fires for a topic or visibility change too, so an unchanged
  // name must leave the map — and the group's order — untouched.
  const isUnchanged = Object.values(conversationsByGroup).some((group) =>
    group.items.some((item) => item.id === channelId && item.name === name),
  )
  if (isUnchanged) return conversationsByGroup

  return patchConversationInGroups(
    conversationsByGroup,
    channelId,
    (item) => ({ ...item, name }),
    // The name is the server's own tie-breaker, so a rename can sort the row past
    // the window we hold — see `patchConversationInGroups`.
    { canSinkPastThePage: true },
  )
}

/**
 * Move a conversation whose participant set just changed out of the sidebar group
 * it was filed under.
 *
 * A conversation is grouped by `dm_key`, the fingerprint of who is in it, so
 * adding someone re-files it under a different set of people — see
 * `regradeDmRoster` on the server. Left alone, the row keeps sitting under the
 * people it started with for everyone who did not press Add, because the only
 * event they get is this one.
 *
 * The row is dropped rather than inserted into the new group: `IChannel` carries
 * none of what a conversation row renders (unread counts, last-message preview,
 * whether it is shared), and the group it lands in is ordered server-side. So the
 * group it left loses it now, the group it joins is marked for a reread if it
 * happens to be loaded, and the middleware refetches the group list — which is
 * the only thing that can surface a group that did not exist a moment ago.
 */
const regroupConversation = (
  conversationsByGroup: IChannelsState['conversationsByGroup'],
  channelId: string,
  dmKey: string,
): IChannelsState['conversationsByGroup'] => {
  const staleGroupId = Object.keys(conversationsByGroup).find(
    (groupId) =>
      groupId !== dmKey &&
      conversationsByGroup[groupId].items.some((item) => item.id === channelId),
  )
  const target = conversationsByGroup[dmKey]
  const isMissingFromTarget = !!target && !target.items.some((item) => item.id === channelId)
  if (!staleGroupId && !isMissingFromTarget) return conversationsByGroup

  const next = { ...conversationsByGroup }
  if (staleGroupId) {
    const stale = conversationsByGroup[staleGroupId]
    next[staleGroupId] = {
      ...stale,
      items: stale.items.filter((item) => item.id !== channelId),
      // `total` counts the whole group, not the loaded page, so it comes down by
      // the one row that left rather than being recounted from what is held.
      total: Math.max(0, stale.total - 1),
    }
  }
  if (isMissingFromTarget) {
    next[dmKey] = { ...target, needsReload: true }
  }
  return next
}

/**
 * Everywhere a changed channel is held: the flat sidebar list, the open channel,
 * and — when it is a grouped conversation — its row inside `conversationsByGroup`.
 *
 * Shared by the mutation's own success (`UPDATE_CHANNEL_SUCCESS`,
 * `ARCHIVE_CHANNEL_SUCCESS`) and by the `CHANNEL_UPDATED` broadcast carrying
 * somebody else's change, so a rename lands identically whether this tab made it
 * or was told about it.
 *
 * `dmKey` is the group the conversation belongs to *now*, and is sent only when
 * the roster changed — a rename or an archive leaves it filed under the same
 * people, so those pass nothing and the grouping is left alone.
 */
/**
 * Patch every loaded `IChannelDetail` that is this channel.
 *
 * Two slots hold one: `activeChannel` (the open conversation) and
 * `settingsChannel` (whatever a settings dialog is editing, which is often NOT
 * the open conversation — that is the whole reason it has a slot of its own).
 * They are patched together because a mutation is a fact about the channel, not
 * about which slot happens to be pointing at it; patching one and forgetting the
 * other is how the dialog came to render the state it was opened with.
 */
const patchLoadedDetails = (
  state: IChannelsState,
  channelId: string,
  patch: (detail: IChannelDetail) => IChannelDetail,
): Pick<IChannelsState, 'activeChannel' | 'settingsChannel'> => ({
  activeChannel:
    state.activeChannel?.id === channelId ? patch(state.activeChannel) : state.activeChannel,
  settingsChannel:
    state.settingsChannel?.id === channelId ? patch(state.settingsChannel) : state.settingsChannel,
})

const applyChannelUpdate = (
  state: IChannelsState,
  channel: IChannel,
  dmKey: string | null = null,
): IChannelsState => {
  // The name is the only field `IChannel` carries that a conversation row has;
  // the group it sits in is the other thing that can change, and only a roster
  // change moves it.
  const renamed = channel.name
    ? renameConversationInGroups(state.conversationsByGroup, channel.id, channel.name)
    : state.conversationsByGroup

  return {
    ...state,
    channels: updateChannelItem(state.channels, channel.id, (item) => ({
      ...item,
      ...channel,
      // A DM's row title is resolved from its participants when it has no stored
      // name of its own (`channels/List`), and `IChannel` carries only the stored
      // one — so a null must not overwrite the resolved title with nothing.
      name: channel.name ?? item.name,
    })),
    ...patchLoadedDetails(state, channel.id, (detail) => ({
      ...detail,
      ...channel,
      name: channel.name ?? detail.name,
    })),
    conversationsByGroup: dmKey ? regroupConversation(renamed, channel.id, dmKey) : renamed,
    // The Conversations section carries the same title, and it is the list most
    // people are looking at when a rename lands — so without this a renamed
    // conversation kept its old name in the sidebar until the next fetch.
    recentConversations: channel.name
      ? patchRecentConversation(state.recentConversations, channel.id, (item) => ({
          ...item,
          name: channel.name ?? item.name,
          // Stamped so the row can flash the change; see `renamedAt` in the row.
          renamedAt: Date.now(),
        }))
      : state.recentConversations,
  }
}

/**
 * The placeholder status shown between pressing send and Arc's first real event.
 * Deliberately the same wording the server opens a run with, so the indicator
 * doesn't visibly re-label itself the moment the socket catches up.
 */
const ARC_PENDING_STATUS = 'Processing your question…'

/**
 * Show "Arc is working…" from the moment a message that will start a run is sent.
 *
 * `arcProgress` was populated only by CHANNEL_ARC_THINKING and the progress
 * fetch, both of which arrive from the server — so the gap between send and the
 * first event rendered as nothing at all. On a brand-new conversation that gap is
 * at its longest (a session has to be created before Arc says anything), which is
 * exactly where it read as "Arc ignored me".
 *
 * The predicate is the one the server actually dispatches on, and the same one
 * `ChannelMessageRow` uses to decide Retry: an @-mention of Arc, or a 1:1 DM with
 * Arc, where no mention is required. Guessing wider would leave a spinner running
 * forever on a message nothing was ever dispatched for.
 *
 * Keyed by the message that summoned Arc, because that is what the server's own
 * events are keyed by.
 */
const seedPendingArcRun = (
  state: IChannelsState,
  saved: IChannelMessageView,
): IChannelsState['arcProgress'] => {
  // A split pane's send seeds here too: the pane's own reduction is discarded beyond its timeline.
  const channel =
    state.activeChannel?.id === saved.channelId
      ? state.activeChannel
      : state.splitPanes.find((pane) => pane.channelId === saved.channelId)?.detail
  if (!channel) return state.arcProgress

  const isOneToOneArcDm = channel.kind === ChannelKind.Dm && channel.hasArc
  if (!isOneToOneArcDm && !mentionsArc(saved.mentions ?? [], channel.arcUserId)) {
    return state.arcProgress
  }

  const current = state.arcProgress[saved.id]

  return {
    ...state.arcProgress,
    // Keyed by the message that summoned Arc. With threads gone that is simply
    // the message itself — there is no root above it to key on.
    [saved.id]: {
      channelId: saved.channelId,
      // Stamped now, not carried: this is the moment the dispatch that has no
      // session yet began, and what bounds how long it is exempt from settling.
      pendingSince: Date.now(),
      isArcWorking: true,
      thinking: ARC_PENDING_STATUS,
      // Everything else is carried, not reset: re-asking in a conversation that
      // already has a completed run must not blank the citations and files.
      turnTexts: current?.turnTexts ?? [],
      citations: current?.citations ?? [],
      sessionId: current?.sessionId ?? null,
      // A new question is a new turn; its first event names it.
      turnId: null,
      outputFiles: current?.outputFiles ?? [],
      unverifiedFileClaim: current?.unverifiedFileClaim ?? false,
      // Cleared rather than carried: it describes how the LAST turn ended, and a
      // new question is not that turn.
      // Carrying it left "Arc was stopped" — and the tail-end-error alert — parked
      // under the answer to the next question, which had ended perfectly well.
      status: null,
      errorDetails: null,
      feedbackType: current?.feedbackType ?? null,
      feedbackComment: current?.feedbackComment ?? null,
      // Which model served the LAST turn says nothing about the one now
      // starting, and leaving it up would attribute the new answer to a
      // fallback that this question never hit.
      modelUsed: null,
      modelFallbackFrom: null,
    },
  }
}

/**
 * Whether a progress entry is the run an event names. By turn when both sides know it, since
 * two mentions in one conversation are two turns of the same session; otherwise by session,
 * which is all an entry before its first event, or an event without a turn, can say.
 */
const isSameArcRun = (
  progress: IChannelArcProgress,
  run: { sessionId: string; turnId?: string | null },
): boolean =>
  progress.turnId && run.turnId
    ? progress.turnId === run.turnId
    : progress.sessionId === run.sessionId

/**
 * Arc's answer landing is what ends a run, so it is what clears the "working"
 * state — for *that* run and nothing else.
 *
 * Matching is by run (see `isSameArcRun`), not by message: Arc's answer and the
 * message that triggered it are two different rows. Matching on the answer's own id
 * leaves the working line up forever.
 *
 * The fallback below exists for the one case session matching cannot cover: a run
 * that answered without ever emitting a status line leaves the entry
 * `seedPendingArcRun` created still carrying `sessionId: null`, so nothing matches
 * and its spinner never stops.
 *
 * That fallback used to clear *every* session-less working entry, which is wrong
 * the moment two questions are in flight at once: each gets its own entry with a
 * null session until its own first event lands, so the first answer to arrive
 * stopped the second question's spinner too. It is now applied only when it is
 * unambiguous — exactly one session-less entry pending, and no entry carrying this
 * run's session. Two pending and we cannot tell which this reply answers, so
 * neither is touched; their own events, or the progress refetch on open, resolve
 * them. A spinner that runs slightly long is recoverable, one that stops early
 * tells the reader Arc gave up.
 *
 * `thinking` is cleared alongside the flag: it is the last status line the run
 * emitted, and leaving it behind is what made "Arc is working… {status}" outlive
 * the answer. The trace is kept — that is the reasoning behind the reply, and it
 * is meant to stay readable.
 */
const clearFinishedArcRun = (
  arcProgress: IChannelsState['arcProgress'],
  message: Pick<IChannelMessageView, 'kind' | 'arcSessionId' | 'arcTurnId'>,
): IChannelsState['arcProgress'] => {
  if (message.kind !== ChannelMessageKind.Arc || !message.arcSessionId) return arcProgress

  const entries = Object.entries(arcProgress)
  const run = { sessionId: message.arcSessionId, turnId: message.arcTurnId }

  const hasRunMatch = entries.some(
    ([, progress]) => progress.isArcWorking && isSameArcRun(progress, run),
  )

  const pendingWithoutSession = entries.filter(
    ([, progress]) => progress.isArcWorking && progress.sessionId === null,
  )

  // Only when nothing carries this run, and only one candidate exists.
  const fallbackId =
    !hasRunMatch && pendingWithoutSession.length === 1 ? pendingWithoutSession[0][0] : null

  return Object.fromEntries(
    entries.map(([messageId, progress]) => {
      const endsThisRun = isSameArcRun(progress, run) || messageId === fallbackId
      return [
        messageId,
        progress.isArcWorking && endsThisRun
          ? { ...progress, isArcWorking: false, thinking: null }
          : progress,
      ]
    }),
  )
}

/**
 * Whether a run the server cannot account for is still young enough to believe.
 *
 * Only ever true for a `seedPendingArcRun` entry: every other path clears the
 * stamp, because learning anything real about a run ends its dispatch. The stamp
 * alone is the predicate — a threaded follow-up carries the previous run's
 * session forward, so a session says nothing about whether this turn has begun.
 */
const isWithinDispatchGrace = (progress: IChannelArcProgress): boolean =>
  progress.pendingSince !== null && Date.now() - progress.pendingSince < ARC_DISPATCH_GRACE_MS

/**
 * Move a channel on a generation, which is what makes every arc-runs response
 * already in flight *for that channel* stale.
 *
 * A live event whose channel cannot be resolved bumps nothing, and that is
 * correct: its entry carries no `channelId` either, so no listing is allowed to
 * settle it in the first place (see `applyLiveArcRuns`).
 */
const bumpArcGeneration = (
  generations: IChannelsState['arcProgressGeneration'],
  channelId: string | null | undefined,
): IChannelsState['arcProgressGeneration'] =>
  channelId ? { ...generations, [channelId]: (generations[channelId] ?? 0) + 1 } : generations

/** What every live CHANNEL_ARC_* progress event names about its run. */
interface ILiveArcEvent {
  rootMessageId?: string
  sessionId?: string
  channelId?: string
  turnId?: string
}

/**
 * One live progress event, applied to the message it reports against. Its arrival is itself the
 * signal that Arc is working and that the dispatch is over. What a turn writes on completion is
 * carried rather than reset: a live event has none of it, and must not blank what the fetch put here.
 */
const applyLiveArcEvent = (
  state: IChannelsState,
  rootMessageId: string,
  event: ILiveArcEvent,
  live: Pick<IChannelArcProgress, 'thinking' | 'turnTexts'>,
): IChannelsState => {
  const current = state.arcProgress[rootMessageId]
  const runChannelId = event.channelId ?? current?.channelId ?? null
  return {
    ...state,
    arcProgressGeneration: bumpArcGeneration(state.arcProgressGeneration, runChannelId),
    arcProgress: {
      ...state.arcProgress,
      [rootMessageId]: {
        channelId: runChannelId,
        pendingSince: null,
        isArcWorking: true,
        ...live,
        citations: current?.citations ?? [],
        sessionId: event.sessionId ?? current?.sessionId ?? null,
        turnId: event.turnId ?? current?.turnId ?? null,
        outputFiles: current?.outputFiles ?? [],
        unverifiedFileClaim: current?.unverifiedFileClaim ?? false,
        status: current?.status ?? null,
        errorDetails: current?.errorDetails ?? null,
        feedbackType: current?.feedbackType ?? null,
        feedbackComment: current?.feedbackComment ?? null,
        modelUsed: current?.modelUsed ?? null,
        modelFallbackFrom: current?.modelFallbackFrom ?? null,
      },
    },
  }
}

/**
 * What the channel says is still running, applied over what the client believes.
 *
 * Authoritative in both directions, which is the point: it starts the lines for
 * runs this client never saw begin — its own, after the slice was dropped on a
 * channel switch or a reload, and a teammate's started while the socket was down
 * — and it stops the ones whose run has since finished, which is the other half
 * of the same gap (a reply that landed during an outage cleared nothing, because
 * `clearFinishedArcRun` only ever hears about it live).
 *
 * Settling is scoped to the channel that answered, because this map is not: a
 * run in any channel the reader belongs to lands here, one channel's listing says
 * nothing about another's, and clearing on it killed live spinners elsewhere —
 * the very symptom this exists to fix.
 *
 * It also will not stop a run the server has not yet named a session for while it
 * is inside the dispatch grace: `seedPendingArcRun`'s entry for a message just
 * sent is genuinely in flight with nothing listable behind it. Past the grace the
 * exemption ends — a dispatch that never created a session posts no reply either,
 * so nothing else would stop the line.
 */
const applyLiveArcRuns = (
  arcProgress: IChannelsState['arcProgress'],
  channelId: string,
  runs: Array<{ messageId: string } & IChannelArcProgress>,
): IChannelsState['arcProgress'] => {
  // `pendingSince` is a client-only stamp the response does not carry, and a run
  // the server can name is by definition no longer waiting on its dispatch.
  const live = new Map<string, IChannelArcProgress>(
    runs.map(({ messageId, ...progress }): [string, IChannelArcProgress] => [
      messageId,
      { ...progress, pendingSince: null },
    ]),
  )

  const settled = Object.entries(arcProgress).map(([messageId, progress]) => {
    const isCovered = progress.channelId === channelId
    const isStillDispatching = isWithinDispatchGrace(progress)
    const isFinished =
      progress.isArcWorking && isCovered && !isStillDispatching && !live.has(messageId)
    return [
      messageId,
      isFinished ? { ...progress, isArcWorking: false, thinking: null } : progress,
    ] as const
  })

  return { ...Object.fromEntries(settled), ...Object.fromEntries(live) }
}

/**
 * A new message arrived in a grouped conversation — move the badges that show it.
 *
 * The mirror image of `applyReadState`. Reading moves counts down, this moves them
 * up, and both have to touch the group total as well as the row: a grouped
 * conversation is deliberately absent from the flat channel list, so the ordinary
 * `channels` update reaches nothing and the unread badge missed every conversation
 * and every Arc chat.
 *
 * The group total is authoritative here and is always bumped. The row inside it is
 * patched only if that page happens to be loaded — an unexpanded group has no rows
 * to patch, and inventing one would put a conversation in the list ahead of the
 * fetch that orders it.
 *
 * Scheduled runs are bumped too when their automation's group is the target: a run
 * that produced something is unread like anything else. What decides whether it
 * reaches a badge is the selector, not this.
 */
const bumpGroupUnread = (
  state: IChannelsState,
  groupId: string,
  channelId: string,
  mentionsMe: boolean,
): IChannelsState => {
  const bump = <T extends { unreadCount: number; mentionCount: number }>(row: T): T => ({
    ...row,
    unreadCount: row.unreadCount + 1,
    mentionCount: mentionsMe ? row.mentionCount + 1 : row.mentionCount,
  })

  const loaded = state.conversationsByGroup[groupId]

  return {
    ...state,
    conversationGroups: {
      ...state.conversationGroups,
      items: state.conversationGroups.items.map((group) =>
        group.dmKey === groupId ? bump(group) : group,
      ),
    },
    scheduledRunGroups: {
      ...state.scheduledRunGroups,
      items: state.scheduledRunGroups.items.map((group) =>
        group.automationId === groupId ? bump(group) : group,
      ),
    },
    conversationsByGroup: loaded
      ? {
          ...state.conversationsByGroup,
          [groupId]: {
            ...loaded,
            items: loaded.items.map((item) => (item.id === channelId ? bump(item) : item)),
          },
        }
      : state.conversationsByGroup,
  }
}

const touchGroupActivity = (
  state: IChannelsState,
  groupId: string,
  channelId: string,
  message: IChannelMessageView,
): IChannelsState => {
  const lastMessageAt = message.createdAt

  return {
    ...state,
    conversationGroups: {
      ...state.conversationGroups,
      items: state.conversationGroups.items.map((group) =>
        group.dmKey === groupId ? { ...group, lastMessageAt } : group,
      ),
    },
    conversationsByGroup: patchConversationInGroups(
      state.conversationsByGroup,
      channelId,
      (item) => ({
        ...item,
        lastMessageAt,
        lastMessagePreview: message.content.slice(0, CHANNEL_PREVIEW_MAX_CHARS),
      }),
      { canSinkPastThePage: false },
    ),
  }
}

const markGroupStaleForNewConversation = (
  conversationsByGroup: IChannelsState['conversationsByGroup'],
  groupId: string,
  channelId: string,
): IChannelsState['conversationsByGroup'] => {
  const loaded = conversationsByGroup[groupId]
  if (!loaded || loaded.needsReload || loaded.items.some((item) => item.id === channelId)) {
    return conversationsByGroup
  }
  return { ...conversationsByGroup, [groupId]: { ...loaded, needsReload: true } }
}

/**
 * A group's rolled-up total can only ever move by the delta of the one row that
 * changed, and never below zero: the total is fetched separately from the rows,
 * so a stale total must not be pushed negative by a delta it never counted.
 */
const applyBadgeDelta = (count: number, delta: number): number => Math.max(0, count + delta)

/**
 * Apply a channel's recomputed unread/mention counts everywhere they are shown.
 *
 * Three slices carry the same number, and only the first was ever updated — which
 * is why a conversation inside a person's group kept its badge after being read:
 * the sidebar it was actually rendered in never heard about it.
 *
 * The group total moves by the row's *delta* rather than being resummed. A group
 * counts every conversation in it, while `conversationsByGroup` holds only the
 * pages that have been fetched, so resumming what is loaded would erase the rest.
 *
 * A conversation whose group was never expanded has no loaded row to diff against
 * — but it *can* have been clicked: notifications and deep links open a channel
 * directly by id. That is what `dmKey` is for. It names the group, and the active
 * channel's detail (GetChannel, fetched before the mark fired) is the row's prior
 * counts — so the rolled-up badge still comes down without the group ever having
 * been opened. Without a matching active channel there is no prior to diff
 * against, and the totals are left for the next `ListConversationGroups` fetch to
 * reconcile, as before.
 */
/**
 * The Conversations section's order, mirroring ListRecentConversations' ORDER BY
 * (`is_pinned DESC, (unread_count > 0) DESC, last_message_at DESC NULLS LAST,
 * id ASC`). Kept in step by hand: a row patched live and a row fetched must not
 * disagree about where it belongs, or the list reshuffles on the next load.
 */
const byPinnedThenUnreadThenRecent = (
  a: IRecentConversationItem,
  b: IRecentConversationItem,
): number => {
  if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1

  const aUnread = a.unreadCount + a.mentionCount > 0
  const bUnread = b.unreadCount + b.mentionCount > 0
  if (aUnread !== bUnread) return aUnread ? -1 : 1

  // Nulls last, matching NULLS LAST above.
  if (a.lastMessageAt !== b.lastMessageAt) {
    if (!a.lastMessageAt) return 1
    if (!b.lastMessageAt) return -1
    return b.lastMessageAt.localeCompare(a.lastMessageAt)
  }

  return a.id.localeCompare(b.id)
}

/**
 * Patch one conversation's badge in the Conversations section.
 *
 * The section is the only place unread is surfaced now, so a row that drifts
 * from the server is unread the reader is never told about. Patched in place
 * rather than refetched because the two events that move it — a message arriving
 * and a conversation being read — both already carry the new counts.
 *
 * A conversation the section is not currently holding is left alone: it sits
 * outside the window the server cut, and inventing a row here — with no name,
 * members or pin state to give it — is not something this reducer can do. The
 * middleware's debounced refetch is what brings those rows in.
 *
 * Rows it does hold are re-sorted, because "unread first, then most recent" is
 * what the section promises and a patch that only rewrites a badge leaves the
 * row sitting where it was.
 */
const patchRecentConversation = (
  recent: IChannelsState['recentConversations'],
  channelId: string,
  patch: (item: IRecentConversationItem) => IRecentConversationItem,
): IChannelsState['recentConversations'] => {
  const current = recent.items.find((item) => item.id === channelId)
  if (!current) return recent

  const patched = patch(current)
  const items = recent.items
    .map((item) => (item.id === channelId ? patched : item))
    .sort(byPinnedThenUnreadThenRecent)
  return {
    ...recent,
    items,
    // Adjusted, never re-summed: the server's total also covers rows past the loaded window.
    unreadTotal: recent.unreadTotal - countedUnread(current) + countedUnread(patched),
  }
}

/** A read lands on the badge's summary too, since its row may not be loaded. */
const withSummaryReadState = (
  recent: IChannelsState['recentConversations'],
  channelId: string,
  unreadCount: number,
  mentionCount: number,
): IChannelsState['recentConversations'] => {
  if (!recent.unreadConversations.some((summary) => summary.id === channelId)) return recent
  return {
    ...recent,
    unreadConversations: recent.unreadConversations.map((summary) =>
      summary.id === channelId ? { ...summary, unreadCount, mentionCount } : summary,
    ),
  }
}

/** Carry a change to a group's settings onto the threads that alert by it. */
const patchGroupThreads = (
  recent: IChannelsState['recentConversations'],
  groupId: string,
  groupSettings: IRecentConversationItem['groupSettings'],
): IChannelsState['recentConversations'] => {
  if (!recent.items.some((item) => item.parentChannelId === groupId)) return recent
  let unreadTotal = recent.unreadTotal
  const items = recent.items.map((item) => {
    if (item.parentChannelId !== groupId) return item
    // A new group setting ends any unpinned thread's exception to the old one (as the
    // server does); a pinned thread can't be muted by its group.
    const patched = {
      ...item,
      groupSettings,
      overridesGroupMute: item.isPinned ? item.overridesGroupMute : false,
    }
    // A group mute silences its threads, so their unread leaves the total.
    unreadTotal += countedUnread(patched) - countedUnread(item)
    return patched
  })
  return { ...recent, items, unreadTotal }
}

const applyReadState = (
  state: IChannelsState,
  channelId: string,
  unreadCount: number,
  mentionCount: number,
  dmKey?: string | null,
): IChannelsState => {
  const channels = updateChannelItem(state.channels, channelId, (channel) => ({
    ...channel,
    unreadCount,
    mentionCount,
  }))

  // The open channel's own snapshot tracks every read-state change, so the
  // fallback below always diffs against what the *previous* action made true —
  // the second mark-read of an open (already-cleared) channel diffs to zero
  // instead of subtracting the original unread a second time.
  // Same object when the counts already match: a new `activeChannel` re-renders the whole
  // open timeline, and mark-read on every send usually changes nothing here.
  const current = state.activeChannel
  const activeChannel =
    current?.id === channelId &&
    (current.unreadCount !== unreadCount || current.mentionCount !== mentionCount)
      ? { ...current, unreadCount, mentionCount }
      : current

  // Which group holds the row and what it read before are the same lookup, so one
  // pass over the expanded groups answers both.
  let groupId: string | null = null
  let unreadDelta = 0
  let mentionDelta = 0

  const conversationsByGroup = Object.fromEntries(
    Object.entries(state.conversationsByGroup).map(([key, group]) => {
      const existing = group.items.find((item) => item.id === channelId)
      if (!existing) return [key, group]

      groupId = key
      unreadDelta = unreadCount - existing.unreadCount
      mentionDelta = mentionCount - existing.mentionCount

      return [
        key,
        {
          ...group,
          items: group.items.map((item) =>
            item.id === channelId ? { ...item, unreadCount, mentionCount } : item,
          ),
        },
      ]
    }),
  )

  // No loaded row: the conversation was opened directly. The group is named by
  // the event's dmKey and the prior counts come from the channel detail — but
  // only the pre-update detail is a valid prior, so this reads state.activeChannel,
  // not the synced copy above.
  if (!groupId && dmKey && state.activeChannel?.id === channelId) {
    groupId = dmKey
    unreadDelta = unreadCount - state.activeChannel.unreadCount
    mentionDelta = mentionCount - state.activeChannel.mentionCount
  }

  const recentConversations = withSummaryReadState(
    patchRecentConversation(state.recentConversations, channelId, (item) => ({
      ...item,
      unreadCount,
      mentionCount,
    })),
    channelId,
    unreadCount,
    mentionCount,
  )

  if (!groupId || (unreadDelta === 0 && mentionDelta === 0)) {
    return { ...state, channels, activeChannel, conversationsByGroup, recentConversations }
  }

  // The key space can't collide (a uuid is never a colon-joined id set), so the
  // same id is safely offered to both group kinds and matches at most one.
  return {
    ...state,
    channels,
    activeChannel,
    conversationsByGroup,
    recentConversations,
    conversationGroups: {
      ...state.conversationGroups,
      items: state.conversationGroups.items.map((group) =>
        group.dmKey === groupId
          ? {
              ...group,
              unreadCount: applyBadgeDelta(group.unreadCount, unreadDelta),
              mentionCount: applyBadgeDelta(group.mentionCount, mentionDelta),
            }
          : group,
      ),
    },
    scheduledRunGroups: {
      ...state.scheduledRunGroups,
      items: state.scheduledRunGroups.items.map((group) =>
        group.automationId === groupId
          ? {
              ...group,
              unreadCount: applyBadgeDelta(group.unreadCount, unreadDelta),
              mentionCount: applyBadgeDelta(group.mentionCount, mentionDelta),
            }
          : group,
      ),
    },
  }
}

/**
 * Put a scheduled message where it now belongs: in the list, in send order, while it is
 * outstanding; gone once it has been sent or cancelled.
 */
const fileScheduledMessage = (
  state: IChannelsState,
  scheduledMessage: IScheduledChannelMessage | undefined,
): IChannelsState => {
  if (!scheduledMessage) return state
  const others = state.scheduledMessages.items.filter((item) => item.id !== scheduledMessage.id)
  const items = OUTSTANDING_SCHEDULED_MESSAGE_STATUSES.includes(scheduledMessage.status)
    ? [...others, scheduledMessage].sort((a, b) => a.sendAt.localeCompare(b.sendAt))
    : others
  return { ...state, scheduledMessages: { ...state.scheduledMessages, items } }
}

// --- reducer -----------------------------------------------------------------

const channelsReducer = (
  state: IChannelsState = initialState,
  action: ChannelsAction,
): IChannelsState => {
  switch (action.type) {
    // Cross-domain: the reducer needs to know who "me" is to decide whether an
    // inbound message should bump an unread badge or a mention badge.
    // The update actions are here for the name, not the id: editing a nickname
    // returns the same payload, and without them a send would keep going out under
    // the old name until the next full page load.
    case PROFILE_SUCCESS:
    case PROFILE_HYDRATED:
    case UPDATE_PROFILE_SUCCESS:
    case UPDATE_AIM_PROFILE_SUCCESS: {
      const data = action.data as
        | {
            id?: string
            firstName?: string
            lastName?: string
            nickname?: string | null
            email?: string
            avatar?: string | null
          }
        | undefined
      const name = displayName(data ?? {})
      const shortName = shortDisplayName(data ?? {})
      return {
        ...state,
        currentUserId: data?.id ?? state.currentUserId,
        currentUserName: name || state.currentUserName,
        currentUserShortName: shortName || state.currentUserShortName,
        currentUserAvatar: data?.avatar ?? state.currentUserAvatar,
      }
    }

    // --- channel list ---

    case actions.LIST_CHANNELS_REQUEST:
      return { ...state, isLoadingChannels: true }

    case actions.LIST_CHANNELS_FAILURE:
      return { ...state, isLoadingChannels: false }

    case actions.LIST_CHANNELS_SUCCESS:
      return {
        ...state,
        isLoadingChannels: false,
        channels: (action.data as IChannelListItem[]) ?? [],
      }

    case actions.BROWSE_CHANNELS_REQUEST:
      return { ...state, browse: { ...state.browse, isLoading: true } }

    case actions.BROWSE_CHANNELS_FAILURE:
      return { ...state, browse: { ...state.browse, isLoading: false } }

    case actions.BROWSE_CHANNELS_SUCCESS: {
      const data = action.data as {
        channels: Array<IChannel & { memberCount: number; isMember: boolean }>
        total: number
      }
      return {
        ...state,
        browse: { channels: data.channels ?? [], total: data.total ?? 0, isLoading: false },
      }
    }

    // --- active channel ---

    case actions.SET_ACTIVE_CHANNEL: {
      const { channelId } = action.data as { channelId: string | null }
      if (channelId === state.activeChannelId) return state
      return {
        ...state,
        activeChannelId: channelId,
        // Drop the previous channel's detail and messages immediately so the UI
        // can't render channel A's history under channel B's header.
        activeChannel: null,
        messages: { ...initialState.messages, channelId, isLoading: !!channelId },
        // Keyed by message id, and every entry holds a full Arc reasoning trace.
        // Nothing pruned it, so it was the one slice that outlived the messages it
        // described — growing for as long as the tab stayed open. The messages it
        // belongs to are being dropped on this line, so it goes with them; the
        // trace is re-fetched by GetArcProgress when a conversation is reopened.
        arcProgress: initialState.arcProgress,
        // Dropped with it, so a response still in flight for the channel being
        // left can never match. The generation is deliberately not reset —
        // rewinding it could make a stale request's recorded value match again.
        arcRunsRequest: null,
        // Same reasoning, much smaller: indicators for a channel we've left are
        // stale the moment we leave, and the entries are never revisited.
        typingUsers: initialState.typingUsers,
        // Belongs to a message sent in the channel being left.
        mentionInvite: initialState.mentionInvite,
      }
    }

    case actions.GET_SETTINGS_CHANNEL_SUCCESS: {
      const detail = action.data as IChannelDetail
      // Guarded against its own request rather than against navigation: the
      // dialog's subject is frequently NOT the open conversation, which is
      // exactly why it cannot share `activeChannel`.
      const requested = (action.params as { channelId?: string } | undefined)?.channelId
      if (requested && detail.id !== requested) return state
      return {
        ...state,
        settingsChannel: detail,
        // The dialog is often opened on the conversation that is already open —
        // a DM's own settings — and this fetch is the freshest read of it.
        // Keyed on the open channel's *id*, not on a detail already sitting
        // there: SET_ACTIVE_CHANNEL drops the previous detail, so a dialog
        // opened right after a switch would otherwise leave the header empty.
        activeChannel: state.activeChannelId === detail.id ? detail : state.activeChannel,
      }
    }

    case actions.GET_CHANNEL_SUCCESS: {
      const detail = action.data as IChannelDetail
      // A late response for a channel the user has already navigated away from
      // must not overwrite the current one — nor, with nothing open, a pane's.
      if (
        detail.id !== state.activeChannelId &&
        (state.activeChannelId || isOpenBesideMain(state, detail.id))
      ) {
        return state
      }
      // A fresh read supersedes any outstanding Arc-model write: its baseline
      // describes a value this response has just replaced.
      return { ...state, pendingArcModelWrite: null, activeChannel: detail }
    }

    case actions.UPDATE_CHANNEL_SUCCESS:
    case actions.ARCHIVE_CHANNEL_SUCCESS:
      return applyChannelUpdate(state, action.data as IChannel)

    case actions.LIST_MEMBERS_SUCCESS: {
      const members = (action.data as IChannelMember[]) ?? []
      const channelId = action.params?.channelId as string | undefined
      if (!state.activeChannel || state.activeChannel.id !== channelId) return state
      return { ...state, activeChannel: { ...state.activeChannel, members } }
    }

    /**
     * A pin now points at a conversation, a channel or a person, so it has to
     * reach every slice that lists one — the flat channel list, the expanded
     * conversation groups, the Conversations section, the People directory and
     * the homepages' Threads blocks. Patched on REQUEST rather than SUCCESS
     * because a pin is a preference with nothing to reconcile: waiting for the
     * round trip left the row visibly unmoved under the click. FAILURE puts it back.
     */
    case actions.SET_PIN_REQUEST:
    case actions.SET_PIN_FAILURE: {
      const target = action.params as
        { targetType: AimPinTargetType; targetId: string; isPinned: boolean } | undefined
      if (!target) return state
      // A failed pin reverts to what it was before the optimistic patch.
      const isPinned = action.type === actions.SET_PIN_REQUEST ? target.isPinned : !target.isPinned

      if (target.targetType === AimPinTargetType.Person) {
        return {
          ...state,
          people: {
            ...state.people,
            items: state.people.items.map((person) =>
              person.userId === target.targetId ? { ...person, isPinned } : person,
            ),
          },
        }
      }

      const pin = <T extends { isPinned: boolean }>(item: T): T => ({ ...item, isPinned })
      return {
        ...state,
        channels: updateChannelItem(state.channels, target.targetId, pin),
        conversationsByGroup: patchConversationInGroups(
          state.conversationsByGroup,
          target.targetId,
          pin,
          // Pinning only ever moves a row up; unpinning is the direction that can
          // drop it out of the loaded window.
          { canSinkPastThePage: !isPinned },
        ),
        recentConversations: patchRecentConversation(
          state.recentConversations,
          target.targetId,
          pin,
        ),
        channelHome: patchHomeConversation(state.channelHome, target.targetId, pin),
        personHome: patchHomeConversation(state.personHome, target.targetId, pin),
        arcHome: patchHomeConversation(state.arcHome, target.targetId, pin),
        // And whichever loaded detail is this channel, which is where the
        // settings dialog reads its Pin/Unpin label from.
        ...patchLoadedDetails(state, target.targetId, pin),
      }
    }

    case actions.UPDATE_SETTINGS_SUCCESS: {
      const data = action.data as {
        channelId: string
        notificationLevel: IChannelListItem['notificationLevel']
        mutedUntil: string | null
        overridesGroupMute?: boolean
      }
      const patchedRow = state.recentConversations.items.find((item) => item.id === data.channelId)
      return {
        ...state,
        channels: updateChannelItem(state.channels, data.channelId, (item) => ({
          ...item,
          notificationLevel: data.notificationLevel,
          mutedUntil: data.mutedUntil,
        })),
        // Same settings, but for a conversation grouped under a person or
        // automation rather than a top-level channel/DM — the settings modal
        // opened on an Arc conversation lands here via the same action. Pinning
        // is no longer one of them; it has its own action above.
        conversationsByGroup: patchConversationInGroups(
          state.conversationsByGroup,
          data.channelId,
          (item) => ({
            ...item,
            notificationLevel: data.notificationLevel,
            mutedUntil: data.mutedUntil,
          }),
          // Neither of these settings reorders the list, so no row can sink out
          // of the loaded window.
          { canSinkPastThePage: false },
        ),
        // The flat list, which is where a thread's Mute is read and toggled —
        // and, for a group, where its threads read the level they alert by.
        recentConversations: withMutedConversationId(
          patchGroupThreads(
            patchRecentConversation(state.recentConversations, data.channelId, (item) => ({
              ...item,
              notificationLevel: data.notificationLevel,
              mutedUntil: data.mutedUntil,
              overridesGroupMute: data.overridesGroupMute ?? item.overridesGroupMute,
            })),
            data.channelId,
            { notificationLevel: data.notificationLevel, mutedUntil: data.mutedUntil },
          ),
          data.channelId,
          patchedRow
            ? isThreadRowMuted({
                ...patchedRow,
                ...data,
                overridesGroupMute: data.overridesGroupMute ?? patchedRow.overridesGroupMute,
              })
            : isConversationMuted(data),
        ),
        // And on whichever loaded detail is this channel — the settings dialog
        // reads its controls from one of them, so without this it would go on
        // rendering the state it was opened with.
        ...patchLoadedDetails(state, data.channelId, (detail) => ({
          ...detail,
          notificationLevel: data.notificationLevel,
          mutedUntil: data.mutedUntil,
          overridesGroupMute: data.overridesGroupMute ?? detail.overridesGroupMute,
        })),
        // An open thread of this group loses its exception the same way.
        ...(state.activeChannel?.parentChannelId === data.channelId && !state.activeChannel.isPinned
          ? { activeChannel: { ...state.activeChannel, overridesGroupMute: false } }
          : {}),
      }
    }

    case actions.LEAVE_CHANNEL_SUCCESS: {
      const channelId = action.params?.channelId as string | undefined
      if (!channelId) return state
      const leftItem = state.recentConversations.items.find((item) => item.id === channelId)
      return {
        ...state,
        channels: state.channels.filter((channel) => channel.id !== channelId),
        // A thread you leave goes from the sidebar with you.
        recentConversations: {
          ...state.recentConversations,
          items: state.recentConversations.items.filter((item) => item.id !== channelId),
          unreadTotal:
            state.recentConversations.unreadTotal - (leftItem ? countedUnread(leftItem) : 0),
        },
        ...(state.activeChannelId === channelId
          ? {
              activeChannelId: null,
              activeChannel: null,
              messages: initialState.messages,
            }
          : {}),
      }
    }

    // --- messages ---

    // Scoped like SUCCESS: a split pane pages its own timeline through the same action.
    case actions.LIST_MESSAGES_REQUEST: {
      if (action.params?.channelId !== state.messages.channelId) return state
      const isLoadMore = !!action.params?.before
      return {
        ...state,
        messages: {
          ...state.messages,
          isLoading: !isLoadMore && state.messages.items.length === 0,
          isLoadingMore: isLoadMore,
          hasFailed: isLoadMore ? state.messages.hasFailed : false,
        },
      }
    }

    case actions.LIST_MESSAGES_FAILURE: {
      if (action.params?.channelId !== state.messages.channelId) return state
      return {
        ...state,
        messages: {
          ...state.messages,
          isLoading: false,
          isLoadingMore: false,
          hasFailed: state.messages.hasFailed || !action.params?.before,
        },
      }
    }

    case actions.LIST_MESSAGES_SUCCESS: {
      const data = action.data as {
        messages: IChannelMessageView[]
        hasMore: boolean
        nextCursor: string | null
        nextCursorId?: string | null
      }
      const channelId = action.params?.channelId as string | undefined
      // Stale page for a channel we've left — discard rather than mixing it in.
      if (channelId !== state.messages.channelId) return state

      const isLoadMore = !!action.params?.before
      const isBackfill = !!action.params?.isBackfill

      return {
        ...state,
        messages: {
          ...state.messages,
          // A first page replaces, bar the sender's own un-sent rows; load-more and
          // post-reconnect backfill merge. Backfill must merge because optimistic
          // and live rows may already be present, and the fetch overlaps them by
          // design.
          items:
            isLoadMore || isBackfill
              ? mergeMessages(state.messages.items, data.messages)
              : withUnsentRows(state.messages.items, data.messages),
          // Paging upward is the only thing that can tell us more history exists;
          // a backfill of recent messages says nothing about the far end.
          hasMore: isBackfill ? state.messages.hasMore : data.hasMore,
          nextCursor: isBackfill ? state.messages.nextCursor : data.nextCursor,
          nextCursorId: isBackfill ? state.messages.nextCursorId : (data.nextCursorId ?? null),
          // A plain first page IS the tail, so it ends any deep-link window and
          // lets live messages append again. Load-more pages upward from the tail
          // and backfill refreshes it, so neither changes that.
          hasNewer: isLoadMore || isBackfill ? state.messages.hasNewer : false,
          isLoading: false,
          hasFailed: false,
          isLoadingMore: false,
        },
      }
    }

    case actions.GET_MESSAGE_CONTEXT_REQUEST:
    case actions.GET_MESSAGE_CONTEXT_FAILURE:
      if (action.params?.channelId !== state.messages.channelId) return state
      return {
        ...state,
        messages: {
          ...state.messages,
          isLoading: action.type === actions.GET_MESSAGE_CONTEXT_REQUEST,
        },
      }

    /**
     * A window from the middle of history, for a deep link whose target was not in
     * the loaded page.
     *
     * Replaces `items` rather than merging: the window and whatever was already
     * loaded are two disjoint stretches of the same channel, and merging them would
     * render a gap as though it were continuous. `hasNewer` carries whether the tail
     * is missing, which drives both the "jump to latest" affordance and the
     * suppression of live appends.
     */
    case actions.GET_MESSAGE_CONTEXT_SUCCESS: {
      const data = action.data as {
        messages: IChannelMessageView[]
        hasMore: boolean
        nextCursor: string | null
        nextCursorId?: string | null
        hasNewer: boolean
        anchorMessageId: string
      }
      const channelId = action.params?.channelId as string | undefined
      if (channelId !== state.messages.channelId) return state
      // The server reads the window from the target's own channel, whatever was asked for: a
      // target from another conversation would replace this timeline with that one's.
      if (data.messages.some((message) => message.channelId !== channelId)) {
        return { ...state, messages: { ...state.messages, isLoading: false } }
      }

      return {
        ...state,
        messages: {
          ...state.messages,
          items: data.messages,
          hasMore: data.hasMore,
          nextCursor: data.nextCursor,
          nextCursorId: data.nextCursorId ?? null,
          hasNewer: data.hasNewer,
          isLoading: false,
          isLoadingMore: false,
        },
      }
    }

    case actions.SEND_MESSAGE_REQUEST: {
      const optimistic = buildOptimisticMessage(state, action.params)
      if (!optimistic) return state

      if (state.messages.channelId !== optimistic.channelId) return state
      return {
        ...state,
        messages: { ...state.messages, items: [...state.messages.items, optimistic] },
      }
    }

    case actions.SEND_MESSAGE_SUCCESS:
    case actions.SEND_VOICE_MEMO_SUCCESS: {
      // `unreachableMentions` rides along with the saved message but isn't part of
      // it, so it's split off rather than stored on the row.
      const { unreachableMentions, ...saved } = action.data as IChannelMessageView & {
        unreachableMentions?: Array<{ id: string; name: string }>
      }
      const tempId = action.params?.tempId as string | undefined

      // Replaces rather than accumulates: the offer is about the message just sent.
      // Set on every path below, including the ones that bail out of reconciling
      // because the user has already moved on — the prompt is keyed to its channel
      // and simply won't render anywhere else.
      const mentionInvite = unreachableMentions?.length
        ? { channelId: saved.channelId, users: unreachableMentions }
        : null

      const reconcile = (items: IChannelMessageView[]) => {
        // Drop the optimistic row, then upsert the saved one. Splitting it this
        // way handles the race where the WebSocket echo already inserted the real
        // message before the HTTP response landed — otherwise the user briefly
        // sees their own message twice.
        const withoutTemp = tempId ? items.filter((item) => item.id !== tempId) : items
        return upsertById(withoutTemp, saved)
      }

      // Seeded here rather than on the optimistic row: the key has to be the id
      // the server will emit its own events against, and the temp id never is.
      const arcProgress = seedPendingArcRun(state, saved)

      if (state.messages.channelId !== saved.channelId) {
        return { ...state, mentionInvite, arcProgress }
      }
      return {
        ...state,
        mentionInvite,
        arcProgress,
        messages: { ...state.messages, items: reconcile(state.messages.items) },
      }
    }

    case actions.SEND_MESSAGE_FAILURE: {
      const tempId = action.params?.tempId as string | undefined
      if (!tempId) return state
      // Mark, don't remove — the typed body stays on screen and can be retried.
      // The server's reason travels with the mark: a rejected message is not a
      // dropped one, and telling someone to check their connection when the body
      // is what was refused sends them after a fault that isn't there.
      //
      // Only the server's own rejection, though — `status` is stamped on every
      // error the api client throws from a response (see client.ts) and is absent
      // from superagent's transport failures, whose message is its own multi-line
      // "Request has been terminated / Possible causes: …" internals. Those are
      // precisely what the connection copy is for.
      const error = action.error
      const failedReason = error?.status === undefined ? undefined : error.message
      const flag = (message: IChannelMessageView) => ({
        ...message,
        isPending: false,
        isFailed: true,
        failedReason,
      })
      const messages = { ...state.messages, items: mapById(state.messages.items, tempId, flag) }

      if (messages.items.some((item) => item.id === tempId)) return { ...state, messages }

      // Nothing to mark: the first message of a new conversation is sent the instant
      // `OpenDm` returns, before that conversation is the one on screen, so
      // SEND_MESSAGE_REQUEST dropped its optimistic row and the send failed in
      // silence into an empty chat. Rebuilt here so it fails like any other message,
      // and only into the conversation it was addressed to.
      const rebuilt = buildOptimisticMessage(state, action.params)
      if (!rebuilt || rebuilt.channelId !== messages.channelId) {
        return { ...state, messages }
      }
      return {
        ...state,
        messages: { ...messages, items: [...messages.items, flag(rebuilt)] },
      }
    }

    /**
     * Each returns the whole saved message, and each is applied from the response
     * rather than from the socket echo they also produce: the user is looking at the
     * thing they just changed, and a round-trip reads as the click not registering.
     */
    case actions.EDIT_MESSAGE_SUCCESS:
    case actions.DISMISS_LINK_PREVIEW_SUCCESS:
    case actions.ACCEPT_TASK_SUGGESTION_SUCCESS:
    case actions.DISMISS_TASK_SUGGESTION_SUCCESS:
    case actions.RETRY_VOICE_MEMO_TRANSCRIPTION_SUCCESS: {
      const updated = action.data as IChannelMessageView
      return { ...state, ...applyMessageUpdate(state, updated) }
    }

    case actions.DELETE_MESSAGE_SUCCESS: {
      const messageId = action.params?.messageId as string | undefined
      if (!messageId) return state
      return { ...state, ...applyMessageDelete(state, messageId) }
    }

    /**
     * Applied optimistically to the open channel. The picker is a control the
     * user is looking at, so waiting for a refetch to reflect their own choice
     * reads as the click not registering.
     */
    case actions.SET_ARC_MODEL_REQUEST: {
      const channelId = action.params?.channelId as string | undefined
      const model = (action.params?.model as string | null | undefined) ?? null
      const requestId = action.params?.requestId as number | undefined
      if (!channelId || state.activeChannel?.id !== channelId) return state
      return {
        ...state,
        // Claimed even when nothing is on screen to roll back, so the newest
        // pick is always the one a failure is allowed to undo.
        pendingArcModelWrite: {
          requestId: requestId ?? 0,
          // Only the first write in a burst has a "before" the server agrees
          // with; later ones inherit it. Tested on the object, not the field —
          // an unpinned conversation's baseline is legitimately null.
          rollbackModel: state.pendingArcModelWrite
            ? state.pendingArcModelWrite.rollbackModel
            : state.activeChannel.arcModel,
        },
        activeChannel: { ...state.activeChannel, arcModel: model },
      }
    }

    /**
     * A write that landed is the new server truth. If it is the one on screen
     * there is nothing left outstanding; if a newer pick has already replaced it,
     * its model becomes the baseline that newer write has to unwind to.
     */
    case actions.SET_ARC_MODEL_SUCCESS: {
      const channelId = action.params?.channelId as string | undefined
      const model = (action.params?.model as string | null | undefined) ?? null
      const requestId = action.params?.requestId as number | undefined
      const pending = state.pendingArcModelWrite
      if (!channelId || state.activeChannel?.id !== channelId || !pending) return state
      if (pending.requestId === requestId) return { ...state, pendingArcModelWrite: null }
      return { ...state, pendingArcModelWrite: { ...pending, rollbackModel: model } }
    }

    /**
     * Put the last persisted choice back when the write is refused. Without this
     * the picker kept showing a model the server never stored, and the correction
     * only arrived on the next open — which reads as the selection spontaneously
     * reverting rather than as a save that failed.
     *
     * Only for the write still on screen: picking twice in quick succession
     * leaves two in flight, and rolling back the older one would drag the picker
     * off the newer pick the server did accept. A superseded refusal leaves the
     * baseline alone too — a write the server rejected moved nothing.
     */
    case actions.SET_ARC_MODEL_FAILURE: {
      const channelId = action.params?.channelId as string | undefined
      const requestId = action.params?.requestId as number | undefined
      const pending = state.pendingArcModelWrite
      if (!channelId || state.activeChannel?.id !== channelId) return state
      if (!pending || pending.requestId !== requestId) return state
      return {
        ...state,
        pendingArcModelWrite: null,
        activeChannel: { ...state.activeChannel, arcModel: pending.rollbackModel },
      }
    }

    case actions.ARCHIVE_CONVERSATION_SUCCESS:
      return { ...state, pendingConversationArchive: null }

    /**
     * The server refused, so the row was never archived — put it back where it
     * was. Without this a rejected write read exactly like a successful one
     * until something unrelated refetched the list.
     */
    case actions.ARCHIVE_CONVERSATION_FAILURE: {
      const stashed = state.pendingConversationArchive
      if (!stashed) return state
      if (state.recentConversations.items.some((item) => item.id === stashed.item.id)) {
        return { ...state, pendingConversationArchive: null }
      }

      const items = [...state.recentConversations.items]
      items.splice(Math.min(stashed.index, items.length), 0, stashed.item)

      return {
        ...state,
        pendingConversationArchive: null,
        recentConversations: {
          ...state.recentConversations,
          items,
          unreadTotal:
            state.recentConversations.unreadTotal +
            (stashed.item.isArchived ? 0 : countedUnread(stashed.item)),
        },
      }
    }

    // --- Conversation groups ---

    case actions.LIST_CONVERSATION_GROUPS_REQUEST:
      return { ...state, conversationGroups: { ...state.conversationGroups, isLoading: true } }

    case actions.LIST_CONVERSATION_GROUPS_SUCCESS: {
      const items = (action.data as IConversationGroup[] | undefined) ?? []
      return { ...state, conversationGroups: { items, isLoading: false } }
    }

    case actions.LIST_CONVERSATION_GROUPS_FAILURE:
      return { ...state, conversationGroups: { ...state.conversationGroups, isLoading: false } }

    case actions.CREATE_CHANNEL_CONVERSATION_SUCCESS: {
      // Flagged for reload rather than spliced in: the group is ordered by last
      // activity and paged, and a brand-new conversation with no messages sorts
      // by a rule this reducer would have to duplicate to place it correctly.
      const channelId = (action.params as { channelId?: string } | undefined)?.channelId
      const group = channelId ? state.conversationsByGroup[channelId] : undefined
      if (!channelId || !group) return state

      return {
        ...state,
        conversationsByGroup: {
          ...state.conversationsByGroup,
          [channelId]: { ...group, needsReload: true },
        },
      }
    }

    /**
     * Drop the row on REQUEST rather than SUCCESS: archiving is a preference
     * with nothing to reconcile, and a row that lingers until the round trip
     * finishes reads as a click that did nothing. It comes back on the next
     * load, which is also what happens when someone posts in it.
     */
    case actions.ARCHIVE_CONVERSATION_REQUEST: {
      const target = action.params as { channelId?: string; isArchived?: boolean } | undefined
      if (!target?.channelId) return state

      const index = state.recentConversations.items.findIndex(
        (item) => item.id === target.channelId,
      )
      if (index === -1) return state
      const archived = state.recentConversations.items[index]
      // Unarchiving moves a row out of the Archived filter, the same way archiving
      // moves it out of every other.
      if (!target.isArchived && !archived.isArchived) return state
      const items = state.recentConversations.items.filter((item) => item.id !== target.channelId)

      return {
        ...state,
        pendingConversationArchive: { item: archived, index },
        recentConversations: {
          ...state.recentConversations,
          items,
          // An archived row was never in the total, so taking it out of view changes nothing.
          unreadTotal:
            state.recentConversations.unreadTotal -
            (archived.isArchived ? 0 : countedUnread(archived)),
        },
      }
    }

    case actions.LIST_RECENT_CONVERSATIONS_REQUEST:
      return { ...state, recentConversations: { ...state.recentConversations, isLoading: true } }

    case actions.LIST_RECENT_CONVERSATIONS_SUCCESS: {
      // A response fetched for another filter or window, finishing late, would show
      // the wrong rows under the current filter. The newer request owns `isLoading`.
      if (isStaleThreadsResponse(action, state)) return state
      const payload = action.data as
        | {
            items: IRecentConversationItem[]
            unreadTotal: number
            hasMore?: boolean
            unreadConversations?: IUnreadConversationSummary[]
            quietThreadIds?: string[]
            mutedConversationIds?: string[]
            mutedUnreadIds?: string[]
            nextMuteExpiry?: string | null
          }
        | undefined
      return {
        ...state,
        recentConversations: {
          ...state.recentConversations,
          items: payload?.items ?? [],
          unreadTotal: payload?.unreadTotal ?? 0,
          isLoading: false,
          hasMore: payload?.hasMore ?? false,
          unreadConversations: payload?.unreadConversations ?? [],
          quietThreadIds: payload?.quietThreadIds ?? [],
          mutedConversationIds: payload?.mutedConversationIds ?? [],
          mutedUnreadIds: payload?.mutedUnreadIds ?? [],
          nextMuteExpiry: payload?.nextMuteExpiry ?? null,
        },
      }
    }

    case actions.SET_THREADS_FILTER: {
      const { filter } = action.data as { filter: ThreadsFilter }
      return {
        ...state,
        recentConversations: {
          ...state.recentConversations,
          filter,
          recentCount: RECENT_CONVERSATIONS_FLOOR,
        },
      }
    }

    case actions.SHOW_FEWER_THREADS:
      return {
        ...state,
        recentConversations: {
          ...state.recentConversations,
          recentCount: RECENT_CONVERSATIONS_FLOOR,
        },
      }

    case actions.SHOW_MORE_THREADS:
      return {
        ...state,
        recentConversations: {
          ...state.recentConversations,
          recentCount: state.recentConversations.recentCount + RECENT_CONVERSATIONS_PAGE,
        },
      }

    case actions.LIST_RECENT_CONVERSATIONS_FAILURE:
      if (isStaleThreadsResponse(action, state)) return state
      return { ...state, recentConversations: { ...state.recentConversations, isLoading: false } }

    case actions.LIST_PEOPLE_REQUEST:
      return { ...state, people: { ...state.people, isLoading: true } }

    case actions.LIST_PEOPLE_SUCCESS:
      return {
        ...state,
        people: {
          items: (action.data as IPersonDirectoryEntry[] | undefined) ?? [],
          isLoading: false,
        },
      }

    case actions.LIST_PEOPLE_FAILURE:
      return { ...state, people: { ...state.people, isLoading: false } }

    case actions.GET_CHANNEL_HOME_REQUEST: {
      const channelId = (action.params as { channelId?: string } | undefined)?.channelId ?? null
      return {
        ...state,
        channelHome: {
          channelId,
          // Cleared rather than kept while the next one loads: the four blocks
          // are per-channel, and holding them would draw another channel's tasks
          // and pins under this one's title.
          data: channelId === state.channelHome.channelId ? state.channelHome.data : null,
          isLoading: true,
          pending: homeRequestOf(action.params),
          hasFailed: false,
        },
      }
    }

    case actions.GET_CHANNEL_HOME_SUCCESS: {
      if (!answersPending(state.channelHome.pending, action.params)) return state
      const data = action.data as IChannelHome | undefined
      return {
        ...state,
        channelHome: {
          channelId: data?.channel.id ?? state.channelHome.channelId,
          data: data ?? null,
          isLoading: false,
          pending: null,
          hasFailed: false,
        },
      }
    }

    case actions.GET_CHANNEL_HOME_FAILURE:
      if (!answersPending(state.channelHome.pending, action.params)) return state
      return {
        ...state,
        channelHome: { ...state.channelHome, isLoading: false, pending: null, hasFailed: true },
      }

    case actions.GET_PERSON_HOME_REQUEST: {
      const userId = (action.params as { userId?: string } | undefined)?.userId ?? null
      return {
        ...state,
        personHome: {
          userId,
          data: userId === state.personHome.userId ? state.personHome.data : null,
          isLoading: true,
          pending: homeRequestOf(action.params),
          hasFailed: false,
        },
      }
    }

    case actions.GET_PERSON_HOME_SUCCESS: {
      if (!answersPending(state.personHome.pending, action.params)) return state
      const data = action.data as IPersonHome | undefined
      return {
        ...state,
        personHome: {
          userId: data?.person.userId ?? state.personHome.userId,
          data: data ?? null,
          isLoading: false,
          pending: null,
          hasFailed: false,
        },
      }
    }

    case actions.GET_PERSON_HOME_FAILURE:
      if (!answersPending(state.personHome.pending, action.params)) return state
      return {
        ...state,
        personHome: { ...state.personHome, isLoading: false, pending: null, hasFailed: true },
      }

    case actions.GET_ARC_HOME_REQUEST:
      return {
        ...state,
        arcHome: {
          ...state.arcHome,
          isLoading: true,
          pending: homeRequestOf(action.params),
          hasFailed: false,
        },
      }

    case actions.GET_ARC_HOME_SUCCESS:
      if (!answersPending(state.arcHome.pending, action.params)) return state
      return {
        ...state,
        arcHome: {
          data: (action.data as IArcHome | undefined) ?? null,
          isLoading: false,
          pending: null,
          hasFailed: false,
        },
      }

    case actions.GET_ARC_HOME_FAILURE:
      if (!answersPending(state.arcHome.pending, action.params)) return state
      return {
        ...state,
        arcHome: { ...state.arcHome, isLoading: false, pending: null, hasFailed: true },
      }

    case actions.SAVE_AIM_NOTE_SUCCESS: {
      // Stamped locally rather than refetched: the notepad already holds the
      // text, and only the byline and time were unknown to it.
      const saved = action.params as
        | {
            scopeType?: AimNoteScopeType
            scopeId?: string
            content?: string
            contentState?: RawDraftContentState | null
          }
        | undefined
      if (!saved?.scopeId) return state

      const note: IAimNote = {
        content: saved.content ?? '',
        contentState: saved.contentState ?? null,
        updatedAt: new Date().toISOString(),
        updatedByName: null,
      }

      /**
       * Only the page whose notepad this is.
       *
       * All three were patched unconditionally, so saving a group's note rewrote
       * the cached note on whichever person's page and Arc's page were also in
       * the store — and `HomeNotepad` deliberately does not reseed its editor for
       * a scope it already has, so the wrong text survived until a reload.
       */
      const isChannelScope = saved.scopeType === AimNoteScopeType.Channel
      const patch = <T extends { data: { note: IAimNote } | null }>(home: T, owns: boolean): T =>
        owns && home.data ? { ...home, data: { ...home.data, note } } : home

      return {
        ...state,
        channelHome: patch(
          state.channelHome,
          isChannelScope && state.channelHome.channelId === saved.scopeId,
        ),
        personHome: patch(
          state.personHome,
          !isChannelScope && state.personHome.userId === saved.scopeId,
        ),
        // Arc's notepad saves under Person scope keyed by the Arc bot's user id.
        arcHome: patch(
          state.arcHome,
          !isChannelScope && state.arcHome.data?.arcUserId === saved.scopeId,
        ),
      }
    }

    case actions.INVALIDATE_LOADED_CONVERSATIONS: {
      const loadedGroups = Object.entries(state.conversationsByGroup)
      if (loadedGroups.length === 0) return state
      return {
        ...state,
        conversationsByGroup: Object.fromEntries(
          loadedGroups.map(([groupId, group]) => [groupId, { ...group, needsReload: true }]),
        ),
      }
    }

    case actions.LIST_CONVERSATIONS_REQUEST: {
      const dmKey = conversationGroupKey(action)
      if (!dmKey) return state
      const current = state.conversationsByGroup[dmKey]
      return {
        ...state,
        conversationsByGroup: {
          ...state.conversationsByGroup,
          [dmKey]: {
            items: current?.items ?? [],
            total: current?.total ?? 0,
            hasMore: current?.hasMore ?? false,
            nextOffset: current?.nextOffset ?? 0,
            isLoading: true,
            needsReload: current?.needsReload ?? false,
            failedLoad: null,
          },
        },
      }
    }

    case actions.LIST_CONVERSATIONS_SUCCESS: {
      const dmKey = conversationGroupKey(action)
      if (!dmKey) return state
      const data = action.data as
        { items: IConversationListItem[]; total: number; hasMore: boolean } | undefined
      if (!data) return state

      // Append only when paging. A fresh load — first expand, or a new search —
      // replaces, so narrowing the filter cannot leave the previous matches
      // stacked above the new ones.
      const isLoadingMore = !!action.params?.loadMore
      const held = state.conversationsByGroup[dmKey]
      const previous = isLoadingMore ? (held?.items ?? []) : []

      // Where this page began, in the server's own reckoning. A fresh load started
      // at zero whatever we held before; a page continued from the cursor we sent.
      const baseOffset = isLoadingMore ? (held?.nextOffset ?? previous.length) : 0

      // Paging is by offset over a pin- and name-sensitive ordering, so a page can
      // overlap what we already hold if the set moved underneath us — a pin toggled
      // in another tab, or a message landing mid-scroll. The pin and rename paths
      // keep their own window in step (see `patchConversationInGroups`); this is the
      // guard for everything else. First occurrence wins, so the order the user
      // has already scrolled past never shuffles.
      const seen = new Set(previous.map((conversation) => conversation.id))
      const incoming = data.items.filter((conversation) => !seen.has(conversation.id))
      const items = [...previous, ...incoming]

      return {
        ...state,
        conversationsByGroup: {
          ...state.conversationsByGroup,
          [dmKey]: {
            items,
            total: data.total,
            // Advanced by the page the server sent, not by what survived the dedupe
            // — see `nextOffset` on the state type. Counting our own rows here is
            // what makes an overlapping page unpassable: the cursor would stall on
            // the repeat and re-read it on every subsequent Show more.
            nextOffset: baseOffset + data.items.length,
            // The server's answer, not one derived from what we hold. It measures
            // `offset + items.length < total` against the offset we sent, so with a
            // truthful cursor it is exact — and it is the only one of the two that
            // can end paging. A walked-out cursor returns an empty page, which
            // leaves `items.length < total` still true whenever the reorder cost us
            // a row, holding up a Show more that can never deliver it. The rows the
            // reorder moved out of reach come back on the next re-expand, which
            // re-lists the group from offset zero.
            hasMore: data.hasMore,
            isLoading: false,
            // A fresh load is the server's own first N rows, so whatever we could
            // not reason about is now settled. Paging cannot clear it: that page
            // was read from the very cursor the flag says to distrust.
            needsReload: isLoadingMore ? (held?.needsReload ?? false) : false,
            failedLoad: null,
          },
        },
      }
    }

    case actions.LIST_CONVERSATIONS_FAILURE: {
      const dmKey = conversationGroupKey(action)
      if (!dmKey || !state.conversationsByGroup[dmKey]) return state
      return {
        ...state,
        conversationsByGroup: {
          ...state.conversationsByGroup,
          [dmKey]: {
            ...state.conversationsByGroup[dmKey],
            isLoading: false,
            // Cleared on the way out, not just on success. The reread is driven by
            // an effect watching this flag and `isLoading`, so leaving it set while
            // `isLoading` falls back to false re-satisfies that effect immediately
            // — a failing endpoint would be asked again as fast as it can refuse.
            // One attempt is enough: the window is stale, not broken, and a
            // re-expand re-lists the group from offset zero anyway.
            needsReload: false,
            failedLoad: action.params?.loadMore ? 'next' : 'first',
          },
        },
      }
    }

    case actions.LIST_SCHEDULED_RUN_GROUPS_REQUEST:
      return { ...state, scheduledRunGroups: { ...state.scheduledRunGroups, isLoading: true } }

    case actions.LIST_SCHEDULED_RUN_GROUPS_SUCCESS: {
      const items = (action.data as IScheduledRunGroup[] | undefined) ?? []
      return { ...state, scheduledRunGroups: { items, isLoading: false } }
    }

    case actions.LIST_SCHEDULED_RUN_GROUPS_FAILURE:
      return { ...state, scheduledRunGroups: { ...state.scheduledRunGroups, isLoading: false } }

    // --- Scheduled messages ---

    case actions.LIST_SCHEDULED_MESSAGES_REQUEST:
      return { ...state, scheduledMessages: { ...state.scheduledMessages, isLoading: true } }

    case actions.LIST_SCHEDULED_MESSAGES_SUCCESS: {
      const items = (action.data as IScheduledChannelMessage[] | undefined) ?? []
      return { ...state, scheduledMessages: { items, isLoading: false } }
    }

    case actions.LIST_SCHEDULED_MESSAGES_FAILURE:
      return { ...state, scheduledMessages: { ...state.scheduledMessages, isLoading: false } }

    case actions.SCHEDULE_MESSAGE_SUCCESS:
    case actions.UPDATE_SCHEDULED_MESSAGE_SUCCESS:
    case actions.CANCEL_SCHEDULED_MESSAGE_SUCCESS:
    case actions.SEND_SCHEDULED_MESSAGE_NOW_SUCCESS:
      return fileScheduledMessage(state, action.data as IScheduledChannelMessage)

    case CHANNEL_SCHEDULED_MESSAGE_CHANGED: {
      const { scheduledMessage } = action.data as { scheduledMessage: IScheduledChannelMessage }
      return fileScheduledMessage(state, scheduledMessage)
    }

    // --- Share links ---

    case actions.RESOLVE_SHARE_REQUEST: {
      const params = action.params as { shareId?: string } | undefined
      return {
        ...state,
        shareLanding: {
          shareId: params?.shareId ?? null,
          data: null,
          isLoading: true,
          isJoining: false,
          error: null,
        },
      }
    }

    case actions.RESOLVE_SHARE_SUCCESS: {
      const data = action.data as IChannelShareLanding | undefined
      if (!data) return state
      return { ...state, shareLanding: { ...state.shareLanding, data, isLoading: false } }
    }

    case actions.RESOLVE_SHARE_FAILURE:
      return {
        ...state,
        shareLanding: {
          ...state.shareLanding,
          isLoading: false,
          // Deliberately not the server's message: resolve answers every failure
          // mode identically so a token can't be probed, and this is the human
          // version of that one answer.
          error: 'This conversation is no longer available.',
        },
      }

    case actions.JOIN_SHARE_REQUEST:
      return { ...state, shareLanding: { ...state.shareLanding, isJoining: true, error: null } }

    case actions.JOIN_SHARE_SUCCESS:
      // Cleared outright rather than marked done: joining ends the landing, and the
      // page navigates into the conversation on the same tick.
      return { ...state, shareLanding: initialState.shareLanding }

    case actions.JOIN_SHARE_FAILURE:
      return {
        ...state,
        shareLanding: {
          ...state.shareLanding,
          isJoining: false,
          error: "Couldn't join that conversation.",
        },
      }

    // --- Arc progress ---

    case actions.ARC_PROGRESS_SUCCESS: {
      const messageId = action.params?.messageId as string | undefined
      const data = action.data as IChannelArcProgress | undefined
      if (!messageId || !data) return state
      // A snapshot taken while a dispatch is still in flight has not seen this
      // run at all — the dispatch writes the turn row after it prepares
      // attachments, so a refetch in that window truthfully describes the turn
      // before it, and taking it verbatim stopped a spinner over a question Arc
      // had not started answering. Bounded by the dispatch grace: past it the
      // snapshot is simply right. Everything else is the server's to say: it is
      // the only thing that knows a run has finished.
      const current = state.arcProgress[messageId]
      const keepWorking = !!current?.isArcWorking && isWithinDispatchGrace(current)
      return {
        ...state,
        arcProgress: {
          ...state.arcProgress,
          [messageId]: keepWorking
            ? // The status line is kept with the flag: the snapshot has no turn to
              // read one off, and a spinner that loses its label mid-run looks
              // like it stalled. The stamp is kept too — it is what expires this.
              {
                ...data,
                isArcWorking: true,
                thinking: data.thinking ?? current.thinking,
                pendingSince: current.pendingSince,
              }
            : { ...data, pendingSince: null },
        },
      }
    }

    case actions.ARC_RUNS_REQUEST: {
      const params = action.params as { channelId?: string; requestId?: string } | undefined
      if (!params?.channelId || !params.requestId) return state
      // Overwritten rather than accumulated: only the newest request is ever
      // eligible to install, so there is no pending-request map to keep or leak.
      return {
        ...state,
        arcRunsRequest: {
          channelId: params.channelId,
          id: params.requestId,
          generation: state.arcProgressGeneration[params.channelId] ?? 0,
        },
      }
    }

    /**
     * What the channel says is live, applied only if it is still allowed to speak.
     *
     * Being authoritative in both directions is what makes an overtaken response
     * harmful: a listing that read no live rows, a run starting and installing its
     * line over the socket, then that older response landing, would settle the run
     * that had just begun. So it installs only if it is the newest request for the
     * channel AND no live Arc event *in that channel* has occurred since the
     * request began — the two conditions the Arc plan slice uses against the same
     * race. Reading the generation per channel is what keeps an unrelated run
     * elsewhere from voiding this response, which left the channel being opened
     * with no indicators at all. A response for a channel already left matches
     * neither; the request is dropped on SET_ACTIVE_CHANNEL.
     */
    case actions.ARC_RUNS_SUCCESS: {
      const data = action.data as
        { channelId: string; runs: Array<{ messageId: string } & IChannelArcProgress> } | undefined
      const requestId = (action.params as { requestId?: string } | undefined)?.requestId
      const request = state.arcRunsRequest
      if (!data || !request) return state

      const isNewest = !!requestId && request.id === requestId
      const isCurrent = (state.arcProgressGeneration[data.channelId] ?? 0) === request.generation
      if (!isNewest || !isCurrent || request.channelId !== data.channelId) return state

      return {
        ...state,
        arcProgress: applyLiveArcRuns(state.arcProgress, data.channelId, data.runs ?? []),
      }
    }

    case CHANNEL_ARC_THINKING: {
      const event = action.data as ILiveArcEvent & { thinking?: string }
      if (!event.rootMessageId) return state
      const current = state.arcProgress[event.rootMessageId]
      return applyLiveArcEvent(state, event.rootMessageId, event, {
        thinking: event.thinking ?? null,
        turnTexts: current?.turnTexts ?? [],
      })
    }

    /**
     * Somebody pressed Stop.
     *
     * Matched on the run (see `isSameArcRun`) rather than the message, so every
     * entry the run touched settles at once — a conversation renders the working line on the
     * message that summoned Arc as well as its reply, and clearing one of the two would
     * leave the other spinning on a run that is over.
     *
     * `status` is set to CANCELLED rather than just dropping the flag, because
     * that is what the row itself now says: a refetch through GetChannelArcProgress
     * returns the same CANCELLED status, so the live state and the reloaded state
     * agree on why the answer never came.
     */
    case CHANNEL_ARC_CANCELLED:
    case CHANNEL_ARC_SETTLED: {
      // SETTLED: Arc chose to stay quiet. The run is over, but nothing was stopped.
      const isCancelled = action.type === CHANNEL_ARC_CANCELLED
      const { sessionId, channelId, turnId } = action.data as {
        sessionId?: string
        channelId?: string
        turnId?: string
      }
      if (!sessionId) return state

      // Every entry the run touched shares its channel, so the first one that
      // matches names it for the older events that carried no `channelId`.
      const runChannelId =
        channelId ??
        Object.values(state.arcProgress).find((progress) => progress.sessionId === sessionId)
          ?.channelId ??
        null

      return {
        ...state,
        arcProgressGeneration: bumpArcGeneration(state.arcProgressGeneration, runChannelId),
        arcProgress: Object.fromEntries(
          Object.entries(state.arcProgress).map(([messageId, progress]) => [
            messageId,
            progress.isArcWorking && isSameArcRun(progress, { sessionId, turnId })
              ? {
                  ...progress,
                  isArcWorking: false,
                  // The last status line the run emitted. Leaving it behind is
                  // what made "Arc is working… {status}" outlive the run.
                  thinking: null,
                  // Matches the row on the server, so a refetch agrees with the screen.
                  status: isCancelled
                    ? AtlasAIMessageStatus.CANCELLED
                    : AtlasAIMessageStatus.COMPLETED,
                }
              : progress,
          ]),
        ),
      }
    }

    case CHANNEL_ARC_TURN_TEXT: {
      const event = action.data as ILiveArcEvent & {
        turnIndex?: number
        text?: string
        toolsCalled?: string[]
        stopReason?: string | null
      }
      const { rootMessageId, turnIndex } = event
      if (!rootMessageId || turnIndex === undefined) return state
      const current = state.arcProgress[rootMessageId]
      const turn: IAtlasAITurnText = {
        turnIndex,
        text: event.text ?? '',
        toolsCalled: event.toolsCalled ?? [],
        stopReason: event.stopReason ?? null,
      }
      // Replace by turnIndex rather than append: a step can be re-emitted (the
      // same turn streams more than once), and appending would show it twice.
      const existing = current?.turnTexts ?? []
      const turnTexts = existing.some((entry) => entry.turnIndex === turnIndex)
        ? existing.map((entry) => (entry.turnIndex === turnIndex ? turn : entry))
        : [...existing, turn]

      return applyLiveArcEvent(state, rootMessageId, event, {
        thinking: current?.thinking ?? null,
        turnTexts,
      })
    }

    // Both directions land the same way: the service returns the recomputed
    // counts, and the sidebar row takes them verbatim rather than guessing.
    case actions.MARK_READ_SUCCESS:
    case actions.MARK_UNREAD_SUCCESS: {
      const data = action.data as {
        channelId: string
        unreadCount: number
        mentionCount: number
        dmKey?: string | null
      }
      return applyReadState(state, data.channelId, data.unreadCount, data.mentionCount, data.dmKey)
    }

    // --- search ---

    case actions.SEARCH_MESSAGES_REQUEST:
      return {
        ...state,
        search: {
          ...state.search,
          query: (action.params?.query as string) ?? '',
          // Scope is half the request identity: "cool" and "cool in #general" are
          // two different searches, and the responses are otherwise identical to
          // look at. Same correlation the `files` slice below does on its channel.
          channelId: (action.params?.channelId as string | undefined) ?? null,
          orderBy: (action.params?.orderBy as SearchOrder | undefined) ?? SearchOrder.Relevance,
          filterKey: searchFilterKeyOf(action.params),
          isLoading: true,
        },
      }

    case actions.SEARCH_CONVERSATIONS_REQUEST:
      return {
        ...state,
        conversationSearch: {
          ...state.conversationSearch,
          requestKey: conversationSearchKeyOf(action.params),
          isLoading: true,
        },
      }

    case actions.SEARCH_CONVERSATIONS_SUCCESS: {
      // A superseded request finishing late must not file its hits under the newer query.
      if (conversationSearchKeyOf(action.params) !== state.conversationSearch.requestKey) {
        return state
      }
      const data = action.data as { results: IConversationSearchResult[] } | undefined
      return {
        ...state,
        conversationSearch: {
          ...state.conversationSearch,
          results: data?.results ?? [],
          isLoading: false,
        },
      }
    }

    case actions.SEARCH_CONVERSATIONS_FAILURE:
      if (conversationSearchKeyOf(action.params) !== state.conversationSearch.requestKey) {
        return state
      }
      return { ...state, conversationSearch: { ...state.conversationSearch, isLoading: false } }

    case actions.SEARCH_MESSAGES_FAILURE:
      // Guarded like SUCCESS, and for the same reason: a superseded request that
      // fails would otherwise clear the spinner belonging to the one still running,
      // and the panel reads "no spinner + no results" as "Nothing matches".
      if (isStaleSearch(action, state)) return state
      return { ...state, search: { ...state.search, isLoading: false } }

    case actions.SEARCH_MESSAGES_SUCCESS: {
      const data = action.data as { results: IChannelSearchResult[]; hasMore: boolean }

      // A response from a request that has since been superseded, finishing late.
      // Storing it would file one search's hits under another's name — which every
      // reader of this slice then believes, since that name is what they check
      // against.
      //
      // Dropped here rather than in the components: the panel, its Load more and
      // the field's suggestions all read this one slice, and a guard per reader is
      // three chances to forget. `isLoading` is deliberately left set — the newer
      // request is still in flight and owns it.
      if (isStaleSearch(action, state)) return state

      const isAppend = !!action.params?.offset
      return {
        ...state,
        search: {
          ...state.search,
          results: isAppend ? [...state.search.results, ...data.results] : data.results,
          hasMore: data.hasMore,
          isLoading: false,
        },
      }
    }

    case actions.CLEAR_SEARCH:
      return {
        ...state,
        search: initialState.search,
        conversationSearch: initialState.conversationSearch,
      }

    case actions.DISMISS_MENTION_INVITE:
      return { ...state, mentionInvite: initialState.mentionInvite }

    // --- pins ---

    case actions.PIN_MESSAGE_SUCCESS:
      return { ...state, ...applyMessageUpdate(state, action.data as IChannelMessageView) }

    case actions.SET_SIDE_PANEL: {
      const { panel } = action.data as { panel: ChannelSidePanel }
      return { ...state, sidePanel: panel }
    }

    // --- WebSocket events ---

    case CHANNEL_MESSAGE_CREATED: {
      const { channelId, message, dmKey } = action.data as {
        channelId: string
        message: IChannelMessageView
        /** The conversation's participant fingerprint; null for a named channel. */
        dmKey?: string | null
      }
      const isOwn = message.senderUserId === state.currentUserId
      const isActive = channelId === state.activeChannelId

      // Ends the run this reply belongs to, and only that one — see
      // clearFinishedArcRun for why a second concurrent question must be left
      // spinning rather than guessed at.
      const arcProgress = clearFinishedArcRun(state.arcProgress, message)

      // A reply landing ends a run, so it moves the generation on for the same
      // reason the CHANNEL_ARC_* events do: an arc-runs response that predates it
      // still believes that run is live and would put its spinner back.
      let nextState =
        arcProgress === state.arcProgress
          ? state
          : {
              ...state,
              arcProgress,
              arcProgressGeneration: bumpArcGeneration(state.arcProgressGeneration, channelId),
            }

      if (
        isActive &&
        nextState.messages.channelId === channelId &&
        // Not while the timeline is a mid-history window from a deep link: the
        // last row on screen is an old message, so appending a live one would
        // draw them as neighbours across a gap of everything in between. The
        // sidebar badge below still updates, and "jump to latest" (or sending)
        // reloads the tail properly.
        !nextState.messages.hasNewer
      ) {
        nextState = {
          ...nextState,
          messages: { ...nextState.messages, items: upsertById(nextState.messages.items, message) },
        }
      }

      // Sidebar preview always updates; badges only for other people's messages in
      // conversations the user isn't currently looking at. Unlike the Atlas chat
      // reducer — which bails entirely when the event isn't for the open session —
      // this keeps every conversation's badge live.
      const mentionsMe =
        !!state.currentUserId && message.mentions.some((m) => m.id === state.currentUserId)
      /**
       * Having a conversation open is no longer the same as having read it.
       *
       * The badge used to skip the open conversation, on the reasoning that you
       * can see the message. You cannot, if the window is behind another one —
       * and that was the complaint. The cursor now moves when you leave or reply
       * (see Messages.tsx), so counting here is what makes the sidebar and the
       * "N new messages" divider agree with the server.
       */
      const shouldBump = !isOwn

      nextState = {
        ...nextState,
        channels: updateChannelItem(nextState.channels, channelId, (channel) => ({
          ...channel,
          lastMessageAt: message.createdAt,
          lastMessagePreview: message.content.slice(0, CHANNEL_PREVIEW_MAX_CHARS),
          unreadCount: shouldBump ? channel.unreadCount + 1 : channel.unreadCount,
          mentionCount: shouldBump && mentionsMe ? channel.mentionCount + 1 : channel.mentionCount,
        })),
      }

      /**
       * A grouped conversation is absent from `channels` on purpose, so the
       * update above cannot reach it — its unread lives on the group row and on
       * whichever page of that group is loaded.
       *
       * `dmKey` arrives on the event precisely so this works without the group
       * having been expanded: the row inside it is only patched if its page is in
       * hand, but the group total moves either way, which is what the nav and FAB
       * badges read.
       */
      if (dmKey) {
        nextState = touchGroupActivity(nextState, dmKey, channelId, message)
        if (shouldBump) {
          nextState = bumpGroupUnread(nextState, dmKey, channelId, mentionsMe)
        }
      }

      /**
       * The Conversations section, which spans both kinds and is the only place
       * unread is surfaced — so a row left stale here is unread nobody is told
       * about. Re-sorted as well as patched: "unread first, then most recent" is
       * the section's whole promise, and a message that only rewrote a badge left
       * the row where it was until the next load.
       */
      nextState = {
        ...nextState,
        recentConversations: patchRecentConversation(
          nextState.recentConversations,
          channelId,
          (item) => ({
            ...item,
            lastMessageAt: message.createdAt,
            lastMessagePreview: message.content.slice(0, CHANNEL_PREVIEW_MAX_CHARS),
            // Without this the new preview text is attributed to whoever sent the
            // previous message, and the avatar stack stays ordered by them.
            lastMessageSenderId: message.senderUserId ?? null,
            unreadCount: shouldBump ? item.unreadCount + 1 : item.unreadCount,
            mentionCount: shouldBump && mentionsMe ? item.mentionCount + 1 : item.mentionCount,
          }),
        ),
      }

      return nextState
    }

    case CHANNEL_MESSAGE_UPDATED: {
      const { message } = action.data as { message: IChannelMessageView }
      return { ...state, ...applyMessageUpdate(state, message) }
    }

    case CHANNEL_MESSAGE_DELETED: {
      const { messageId } = action.data as { messageId: string }
      return { ...state, ...applyMessageDelete(state, messageId) }
    }

    case CHANNEL_REACTION_CHANGED: {
      const { messageId, reactions } = action.data as {
        messageId: string
        reactions: IChannelMessageReactionSummary[]
      }
      const setReactions = (message: IChannelMessageView) => ({ ...message, reactions })
      return {
        ...state,
        messages: {
          ...state.messages,
          items: mapById(state.messages.items, messageId, setReactions),
        },
      }
    }

    case CHANNEL_UPDATED: {
      // `dmKey` arrives only when the participant set changed, which is the one
      // case the conversation has to move between sidebar groups.
      const { channel, dmKey } = action.data as { channel: IChannel; dmKey?: string | null }
      return applyChannelUpdate(state, channel, dmKey ?? null)
    }

    case CHANNEL_MEMBER_JOINED:
    case CHANNEL_MEMBER_LEFT: {
      const { channelId, actorUserId, dmKey } = action.data as {
        channelId: string
        actorUserId: string
        dmKey?: string | null
      }
      const joined = action.type === CHANNEL_MEMBER_JOINED
      const conversationsByGroup =
        joined && dmKey
          ? markGroupStaleForNewConversation(state.conversationsByGroup, dmKey, channelId)
          : state.conversationsByGroup

      // Either loaded detail may be this channel: the settings dialog lists the
      // roster it is editing, which is frequently not the open conversation.
      if (state.activeChannel?.id !== channelId && state.settingsChannel?.id !== channelId) {
        return conversationsByGroup === state.conversationsByGroup
          ? state
          : { ...state, conversationsByGroup }
      }
      const actor = (action.data as { actor?: IChannelMember }).actor
      const applyRoster = (detail: IChannelDetail): IChannelDetail => {
        const members = joined
          ? actor && !detail.members.some((m) => m.userId === actorUserId)
            ? [...detail.members, { ...actor, userId: actorUserId, role: ChannelMemberRole.Member }]
            : detail.members
          : detail.members.filter((m) => m.userId !== actorUserId)

        return {
          ...detail,
          members,
          memberCount: members.length,
          // Derived rather than carried on the event: `hasArc` is computed from
          // the roster server-side (channels/Get), so a membership change that
          // isn't followed by a refetch would otherwise leave it asserting Arc is
          // still here — which is what `isOneToOneArcDm` above reads to decide a
          // message reaches Arc without an explicit mention.
          hasArc: !!detail.arcUserId && members.some((m) => m.userId === detail.arcUserId),
        }
      }

      return {
        ...state,
        conversationsByGroup,
        ...patchLoadedDetails(state, channelId, applyRoster),
      }
    }

    case CHANNEL_READ_STATE_CHANGED: {
      // Another tab or device of the same user moved their read cursor — track it
      // here too rather than waiting for a refetch. The counts come from the
      // event because the cursor moves both ways: assuming zero would wipe the
      // badge a mark-unread had just put there.
      const { channelId, unreadCount, mentionCount, dmKey } = action.data as {
        channelId: string
        unreadCount: number
        mentionCount: number
        dmKey?: string | null
      }
      return applyReadState(state, channelId, unreadCount, mentionCount, dmKey)
    }

    case CHANNEL_TYPING_START: {
      const { channelId, userId, name, avatar } = action.data as {
        channelId: string
        userId: string
        name: string
        avatar: string | null
      }
      const existing = state.typingUsers[channelId] ?? []
      const others = existing.filter((user) => user.userId !== userId)
      return {
        ...state,
        typingUsers: {
          ...state.typingUsers,
          // startedAt is refreshed on a repeat START so the UI can expire a
          // stale indicator when someone closes their laptop mid-sentence.
          [channelId]: [...others, { userId, name, avatar, startedAt: Date.now() }],
        },
      }
    }

    case CHANNEL_TYPING_STOP: {
      const { channelId, userId } = action.data as { channelId: string; userId: string }
      const existing = state.typingUsers[channelId] ?? []
      return {
        ...state,
        typingUsers: {
          ...state.typingUsers,
          [channelId]: existing.filter((user) => user.userId !== userId),
        },
      }
    }

    default:
      return state
  }
}

const withMutedUnreadId = (state: IChannelsState, channelId: string, hasUnread: boolean) => {
  const { mutedUnreadIds, mutedConversationIds } = state.recentConversations
  const isListed = mutedUnreadIds.includes(channelId)
  const shouldList = hasUnread && mutedConversationIds.includes(channelId)
  if (isListed === shouldList) return state
  return {
    ...state,
    recentConversations: {
      ...state.recentConversations,
      mutedUnreadIds: shouldList
        ? [...mutedUnreadIds, channelId]
        : mutedUnreadIds.filter((id) => id !== channelId),
    },
  }
}

/**
 * Keeps the muted-unread ids current between refetches: a message landing in a muted
 * conversation adds it, and reading that conversation takes it off again.
 */
const trackMutedUnread = (state: IChannelsState, action: ChannelsAction): IChannelsState => {
  switch (action.type) {
    case CHANNEL_MESSAGE_CREATED: {
      const { channelId, message } = action.data as {
        channelId: string
        message: IChannelMessageView
      }
      // Same rule as the unread count: anyone's message but your own. An open
      // conversation counts too; reading it takes the dot off again.
      const isNew = message.senderUserId !== state.currentUserId
      return isNew ? withMutedUnreadId(state, channelId, true) : state
    }
    case actions.MARK_READ_SUCCESS:
    case actions.MARK_UNREAD_SUCCESS:
    case CHANNEL_READ_STATE_CHANGED: {
      const { channelId, unreadCount } = action.data as { channelId: string; unreadCount: number }
      return withMutedUnreadId(state, channelId, unreadCount > 0)
    }
    default:
      return state
  }
}

export default withSplitPanes(
  withOfflineState((state: IChannelsState | undefined, action: ChannelsAction): IChannelsState =>
    trackMutedUnread(channelsReducer(state, action), action),
  ),
)
